// External imports
import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Prompt } from 'react-router-dom';
import {
  Modal,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  withStyles,
  Snackbar,
  TextField,
  MenuItem,
  Typography,
  Menu,
  Slide,
} from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
// eslint-disable-next-line no-unused-vars
import _ from 'lodash';
import moment from 'moment-timezone';
import 'jquery-ui/ui/widgets/autocomplete';
import get from 'lodash-es/get';
// Internal imports
import ErrorHelpers from '~/app/errorHelpers.js';
import appTheme from '~/themes/GenericTheme.jsx';
import { selectors as returnProfileSelectors } from '~/app/redux/returnProfile/selectors';
import {
  HIERARCHY_TYPE,
  VERIFY_MODAL_TABS,
  TEXTLINK_ERROR_MESSAGE,
  ENCRYPTED_PDF_ERROR_MESSAGES,
  REQUEST_PDF_MSG,
  LOGIN_SETTINGS,
  PRIOR_YEAR_NOTICE_MODE,
} from '~/app/constants';
import XlinkAPI from '~/app/api/xlinkAPI';
import PriorYearNoticeModal from '#/PriorYearNotice/PriorYearNoticeModal.jsx';
import {
  lockReturnWithPriorYearNotice,
  acknowledgePriorYearNotice,
} from '#/PriorYearNotice/priorYearNoticeHelpers.js';
import ErrorRejectModal from './components/ErrorRejectModal.jsx';
import AddFormModal from './components/AddFormModal.jsx';
import K1ManagerModal from './components/K1Manager/K1ManagerModal.jsx';
import ChoiceListModal from './components/ChoiceList/ChoiceListModal.jsx';
import RemoteInvoiceModal from './components/RemoteInvoice/RemoteInvoiceModal.jsx';
import SideBarForms from './components/SideBarForms/SideBarForms.jsx';
import HeaderBarForms from './components/HeaderBarForms/HeaderBarForms.jsx';
import Spinner from '#/Common/Spinner.jsx';
import PrintComponents from '~/app/Pages/Returns/components/Print/PrintComponents.jsx';
import PrintStateAcks from '~/app/Pages/Returns/components/Print/PrintStateAcks.jsx';
import PrintStateClientLetters from '~/app/Pages/Returns/components/Print/PrintStateClientLetters.jsx';
import QueueReturn from './components/Efile/QueueReturn.jsx';
import AttachDocumentModal from './components/Document/AttachDocumentModal/AttachDocumentModal.jsx';
import ImportStockTransactions from './components/ImportStockTransactions/ImportStockTransactions.jsx';
import AssetList from './components/Depreciation/AssetList.jsx';
import SignatureCapture from './components/SignatureCapture.jsx';
import TextLinkSendModal from './components/TextLinkSendModal.jsx';
import TextLinkReceivedModal from './components/TextLinkReceivedModal/TextLinkReceivedModal.jsx';
import PrintDialog from '~/app/Pages/Returns/components/Print/PrintActionsDialog.jsx';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import UtilityAPI from '~/app/api/utilityAPI';
import SuggestedPasswordSaveDialog from '~/app/Pages/Returns/components/Print/SuggestedPasswordSave.jsx';
import SuggestedPasswordDialog from '~/app/Pages/Returns/components/Print/SuggestedPasswordDialog.jsx';
import SuggestedPasswordDoc from '~/app/Pages/Returns/components/Print/SuggestedPasswordDoc.jsx';
import CapturePayment from '~/app/Pages/Payments/PaymentModal/CapturePayment.jsx';
import MessageDialog from './components/MessageDialog/MessageDialog.jsx';
import TransferData from '~/app/Pages/Returns/components/TransferData/TransferData.jsx';
import ArchivedDocuments from './components/Document/ArchivedDocuments/ArchivedDocuments.jsx';
import EventLog from './components/Events/EventLog.jsx';
import AddAsset from './components/Depreciation/AddAsset.jsx';
import Signatures from '~/app/Pages/Returns/pages/Signatures/Signatures.jsx';
import RemoteSignatureModal from '~/app/Pages/Returns/components/Print/RemoteSignatureModal.jsx';
import SubformViewer from './components/SubFormViewer/SubformViewer.jsx';
import Notifications from '~/app/Components/Common/Notifications/Notifications.jsx';
import RefreshToken from '~/app/Components/Common/RefreshToken.jsx';
import WebHelpers, { statusOK } from '~/app/webHelpers.js';
import BookmarkField from './components/BookmarkField.jsx';
import Bookmarks from './components/Bookmarks.jsx';
import ReturnNotes from './components/ReturnNotes.jsx';
import VisionAssistModal from './components/VisionAssist/VisionAssistModal.jsx';
import { handleSuddenCloseWindow } from '~/app/webHelpers';
import { tabListDefinitions } from './DataModel/FormViewer';
import AccountInfoBanner from '~/app/Components/Common/AccountInfoBanner/AccountInfoBanner.jsx';
import FieldInfo from '~/app/Pages/Returns/components/FieldInfo/fieldInfo.jsx';
import { isShortScreenRez } from '~/app/Utility/browser';
import { isSuperOrTechUserReadOnly, formatMaskSSNEIN } from '~/app/Utility/general.js';
import PreviewTermsAndConditions from '~/app/Pages/TermsAndConditions/PreviewTermsAndConditions.jsx';
// Redux imports
import { actions as appActions } from '~/app/redux/modules/app';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';
import { actions as returnProfileActions } from '~/app/redux/returnProfile/duck';
import { actions as overviewActions } from '~/app/redux/modules/overview';
import { actions as messageActions } from '~/app/redux/modules/messages';
import { actions as drilldownActions } from '~/app/redux/drilldown/duck';
import { actions as notificationActions } from '~/app/redux/notifications/duck';
import { actions as remoteSignActions } from '~/app/redux/remoteSign/duck';
import { actions as remoteDocActions } from '~/app/redux/modules/remoteDocRequests.js';
import { actions as returnListActions } from '~/app/redux/returnList/duck';
import { actions as loginSetupActions } from '~/app/redux/loginSetup/duck';
import { actions as defaultsActions } from '~/app/redux/setupPages/defaultsPage/duck';
import { actions as wizardEstimatorActions } from '~/app/redux/modules/wizardEstimator.js';
import { selectors as loginSelector } from '~/app/redux/loginSetup/selectors';
import { store } from '~/app/redux/index';
// Styling imports
import { styles } from './css/formViewer.js';
import './css/formViewer.css';
// Image imports
import downloadIcon from '~/images/icons/downloadIcon.svg';
import lockIcon from '~/images/icons/icons8-lock_2.svg';
import menuVertical from '~/images/icons/menu-vertical-filled.png';
import { SPIN_LOAD_MSG } from './hooks/useSpinner';

const NOTES_REMINDER = 'INFO: Please review return notes';

const AutoSaveInterval = 120000; // ms

// Asset Form format (ex: A00001)- TODO: temporary, used for asset calc flag
const assetFormRegExp = /^[A]{1}[0-9]{5}/;

const ButtonMenuElement = ({ buttonItems, btnMenuOpen, openButtonMenu, closeButtonMenu }) => {
  return (
    <Fragment>
      <Button
        className="inputFormBtn"
        id="buttonMenuForReturns"
        aria-owns="btnMenuCtxReturn"
        aria-haspopup="true"
        style={{
          minWidth: '0.5rem',
          maxWidth: '0.5rem',
          minHeight: 'unset',
          padding: '0px 5px 0px 5px',
          border: '1px solid #0077FF',
          borderRadius: '0px 5px 5px 0px',
          boxShadow: 'none',
          position: 'absolute',
          zIndex: 10,
          '&:focus': {
            border: 'none',
            outline: 'none',
          },
        }}
        color="primary"
        variant="contained"
        onClick={openButtonMenu}
      >
        <img src={menuVertical} style={{ height: `1em` }} />
      </Button>
      <Menu
        id="btnMenuCtxReturn"
        open={btnMenuOpen}
        onClose={closeButtonMenu}
        anchorEl={document.getElementById('buttonMenuForReturns')}
        PaperProps={{
          style: {
            transform: 'translate(-50%, 2.3rem)',
          },
        }}
      >
        {buttonItems.map((btn, index) => {
          return (
            <MenuItem
              onClick={() => {
                closeButtonMenu();
                btn.func();
              }}
              key={'btnMenu' + index}
            >
              {btn.name}
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
};

const mapStateToProps = state => {
  return {
    attachedStatesCount: returnProfileSelectors.getAttachedStatesCount(state),
    alert: state.app.alert,
    addFormModalOpen: state.formViewer.addFormModalOpen,
    choiceListModalOpen: state.formViewer.choiceListModalOpen,
    addDocumentModalOpen: state.formViewer.addDocumentModalOpen,
    documentsToBeAttached: state.formViewer.documentsToBeAttached,
    messageDialogOpen: state.formViewer.messageDialogOpen,
    messageDialogInfo: state.formViewer.messageDialogInfo,
    errorRejectModalOpen: state.formViewer.errorRejectModalOpen,
    showVerifySuccessDlg: state.formViewer.showVerifySuccessDlg,
    showVerifyModalSuccessDlg: state.formViewer.showVerifyModalSuccessDlg,
    eventLogModal: state.formViewer.eventLogModalOpen,
    isK1ManagerModalOpen: state.formViewer.isK1ManagerModalOpen,
    addFormList: state.formViewer.addFormList,
    activityList: state.formViewer.activityList,
    addAssetModal: state.formViewer.addAssetModalOpen,
    choiceListTitle: state.formViewer.choiceListTitle,
    choiceListIniField: state.formViewer.choiceListIniField,
    choiceListData: state.formViewer.choiceListData,
    choiceListFields: state.formViewer.choiceListFields,
    choiceListColumns: state.formViewer.choiceListColumns,
    isTransferDataModalOpen: state.formViewer.isTransferDataModalOpen,
    isCurrentYearModalOpen: state.formViewer.isCurrentYearModalOpen,
    pyReturns: state.formViewer.pyReturns,
    cyReturns: state.formViewer.cyReturns,
    existingReturnType: state.formViewer.existingReturnType,
    stateAcks: state.formViewer.stateAcks,
    attachedStates: state.formViewer.attachedStates,
    errorDialog: state.formViewer.errorDialogOpen,
    errorDialogMessage: state.formViewer.errorDialogMessage,
    errorDialogTitle: state.formViewer.errorDialogTitle,
    printDialog: state.formViewer.printDialogOpen,
    signatureCapture: state.formViewer.signatureCaptureModalOpen,
    clientName: state.returnProfile.clientName,
    clientFilingStatus: state.returnProfile.clientFilingStatus,
    activeForm: state.returnProfile.initialForm,
    activeFile: state.returnProfile.initialFile,
    attachments: state.returnProfile.attachments,
    returnID: state.returnProfile.returnID,
    friendlyID: state.returnProfile.friendlyID,
    loginID: state.app.loggedInUser.userID,
    isReadOnly: state.returnProfile.isReadOnly,
    lockedBy: state.returnProfile.lockedBy,
    lockedViaAccessLevel: state.returnProfile.lockedViaAccessLevel,
    isNew: state.returnProfile.isNew,
    startInterview: state.loginSetup.loginSettings.startInterview?.value,
    loginSettings: state.loginSetup.loginSettings,
    priorYearNotice: state.formViewer.priorYearNotice,
    lockedStatus: state.returnProfile.lockedStatus,
    formList: state.returnProfile.formList,
    remoteSignatureSecondDialog: state.formViewer.remoteSignatureSecondDialogOpen,
    remoteSignatureDialog: state.formViewer.remoteSignatureDialogOpen,
    isRMSSigningMethod: state.formViewer.isRMSSigningMethod,
    isIODSigningMethod: state.formViewer.isIODSigningMethod,
    isTrainingMode: state.loginSetup.isTrainingMode,
    textLinkModalOpen: state.formViewer.textLinkModalOpen,
    textLinkReceivedModalOpen: state.formViewer.textLinkReceivedModalOpen,
    isTextLinkActivated: state.formViewer.isTextLinkActivated,
    textLinkModalContent: state.formViewer.textLinkModalContent,
    textLinkModalContentOriginal: state.formViewer.textLinkModalContentOriginal,
    textLinkNotActivatedDlg: state.formViewer.textLinkNotActivatedDlg,
    textLinkNoClients: state.formViewer.textLinkNoClients,
    textlinkSendSuccess: state.formViewer.textlinkSendSuccess,
    currentView: state.drilldown.drilldownHistory[state.drilldown.drilldownHistory.length - 1],
    ddh: state.drilldown.drilldownHistory,
    autoSaveAfterMins: state.defaultsPageState.autoSaveAfterMins,
    autoSaveEnabled: state.defaultsPageState.autoSaveEnabled,
    silentlySaveReturnOnClose: state.defaultsPageState.silentlySaveReturnOnClose,
    enableDoubleEntry: state.defaultsPageState.enableDoubleEntry,
    requireAutoAdd1040Sch123: state.defaultsPageState.requireAutoAdd1040Sch123,
    printPDFWarningMessageID: state.defaultsPageState.printPDFWarningMessageID,
    printPDFWarningMessage: state.defaultsPageState.printPDFWarningMessage,
    notificationsCount: state.notifications.notifications.length,
    notificationID: state.notifications.selectedNotificationID,
    notificationReturnID: state.notifications.selectedReturnID,
    estimatorBlob: state.formViewer.estimatorState
      ? state.formViewer.estimatorState.blob
      : undefined,
    estimatorDatastore: state.formViewer.estimatorState
      ? state.formViewer.estimatorState.datastore
      : undefined,
    addFormFirst: state.formViewer.addFormFirst,
    addFormPkg: state.formViewer.addFormPkg,
    attachmentsListMostRecent: state.formViewer.attachmentsListUpdated,
    submitForReviewDlg: state.formViewer.submitForReviewDlg,
    readyForReview: state.returnProfile.readyForReview,
    usingDemoAccount: state.returnProfile.usingDemoAccount,
    suggestedPswDialog: state.formViewer.suggestedPasswordModalOpen,
    encryptedPassword: state.formViewer.encryptedPassword,
    encryptedPdfTitle: state.formViewer.encryptedPdfTitle,
    suggestedPswSaveDialog: state.formViewer.suggestedPasswordSaveModalOpen,
    taxpayerEmailForPdf: state.formViewer.taxpayerEmailForPdf,
    spouseEmailForPdf: state.formViewer.spouseEmailForPdf,
    preparerEmailForPdf: state.formViewer.preparerEmailForPdf,
    encryptedPdfSelected: state.formViewer.encryptedPdfSelected,
    encryptedPdfDialogModalOpen: state.formViewer.encryptedPdfDialogModalOpen,
    emailBlob: state.formViewer.emailBlob,
    attachmentContent: state.formViewer.attachmentContent,
    taxpayerChecked: state.formViewer.taxpayerChecked,
    spouseChecked: state.formViewer.spouseChecked,
    representativeChecked: state.formViewer.representativeChecked,
    taxpayerEPassChecked: state.formViewer.taxpayerEPassChecked,
    spouseEPassChecked: state.formViewer.spouseEPassChecked,
    representativeEPassChecked: state.formViewer.representativeEPassChecked,
    sentSuccessTitle: state.formViewer.sentSuccessTitle,
    sentSuccessMsg: state.formViewer.sentSuccessMsg,
    missingInfoModalOpen: state.formViewer.missingInfoModalOpen,
    taxpayerCellPhoneNumber: state.formViewer.taxpayerCellPhoneNumber,
    taxpayerCellPhoneCarrier: state.formViewer.taxpayerCellPhoneCarrier,
    taxpayerCellPhoneDomain: state.formViewer.taxpayerCellPhoneDomain,
    spouseCellPhoneNumber: state.formViewer.spouseCellPhoneNumber,
    spouseCellPhoneCarrier: state.formViewer.spouseCellPhoneCarrier,
    spouseCellPhoneDomain: state.formViewer.spouseCellPhoneDomain,
    spouseInfoAvailable: state.formViewer.spouseInfoAvailable,
    taxpayerTextChecked: state.formViewer.taxpayerTextChecked,
    spouseTextChecked: state.formViewer.spouseTextChecked,
    representativeTextChecked: state.formViewer.representativeTextChecked,
    taxpayerAllowText: state.formViewer.taxpayerAllowText,
    spouseAllowText: state.formViewer.spouseAllowText,
    pdfModalSuccess: state.formViewer.pdfModalSuccess,
    remoteSignSuccessful: state.formViewer.remoteSignSuccessful,
    isSignatureRequestActive: state.formViewer.isSignatureRequestActive,
    onScreen: state.loginSetup.loginSettings.onScreen.value,
    preferredSignaturePad: state.formViewer.preferredSignaturePad,
    returnOpen: state.app.returnOpen,
    officeProfile: state.officeProfile,
    eroData: state.remoteSign.eroData,
    preparerData: state.remoteSign.preparerData,
    amendedPreparerData: state.remoteSign.amendedPreparerData,
    initialSigneeData: state.remoteSign.initialSigneeData,
    messageBadgeCount: state.overview.messageBadgeCount,
    initialForm: state.returnProfile.initialForm,
    initialFile: state.returnProfile.initialFile,
    taxYear: state.returnProfile.year,
    verifyList: state.returnProfile.verifyList,
    rejectList: state.returnProfile.rejectList,
    importErrorList: state.returnProfile.importErrorList,
    bankRejectList: state.returnProfile.bankRejectList,
    alertList: state.returnProfile.alertList,
    errorNumber: state.returnProfile.errorNumber,
    warningNumber: state.returnProfile.warningNumber,
    primarySSNEIN: state.returnProfile.primarySSNEIN,
    isWizard: state.returnProfile.isWizard,
    wizardCompleted: state.returnProfile.wizardCompleted,
    wizardCurrentStep: state.wizardEstimator.wizardCurrentStep,
    returnStatus: state.returnProfile.returnStatus,
    setSubDialogListOpened: state.formViewer.setSubDialogListOpened,
    remoteInvoiceModalOpen: state.formViewer.remoteInvoiceModalOpen,
    activeErrorRejectTab: state.formViewer.activeErrorRejectTab,
    activeAccessLevels: loginSelector.getActiveAccessLevels(state),
    archiveDocId: state.formViewer.archiveDocId,
  };
};

const mapDispatchToProps = {
  ...appActions,
  ...formViewerActions,
  ...returnProfileActions,
  ...overviewActions,
  ...drilldownActions,
  ...messageActions,
  ...notificationActions,
  ...remoteSignActions,
  ...remoteDocActions,
  ...returnListActions,
  updateLoginSettings: loginSetupActions.updateLoginSettings,
  setLoginSettings: loginSetupActions.setLoginSettings,
  ...defaultsActions,
  ...wizardEstimatorActions,
};

/**
 * Page to display the tax return.
 *
 * @component FormViewer
 * @category Returns
 */
const FormViewer = props => {
  // ─── JWT payload — stable for the lifetime of this session ───────────────
  const payload = useMemo(() => WebHelpers.getJWTPayload(), []);

  // ─── Identity block stamped on printed listings ───────────────────────────
  // Shared by the Preparer Notes, Event Log, Document Archive and K-1 Manager
  // print buttons so a printed page stays attributable to a return/preparer.
  const printMeta = useMemo(
    () => ({
      clientName: props.clientName,
      // Hyphenated so the mask the print helper applies lands on the right
      // digits ('XXX-XX-6789' / 'XX-XXX6789' rather than a bare digit run).
      ssnein: props.primarySSNEIN
        ? formatMaskSSNEIN(props.primarySSNEIN, '0', payload?.is_business)
        : '',
      taxYear: props.taxYear,
      returnID: props.friendlyID,
      preparer: payload?.login_id,
    }),
    [props.clientName, props.primarySSNEIN, props.taxYear, props.friendlyID, payload],
  );

  // ─── State ────────────────────────────────────────────────────────────────
  const [loadEstimatorState, setLoadEstimatorState] = useState(false);
  const [unreadTextMessageCount, setUnreadTextMessageCount] = useState(0);

  // ─── Instance-var refs ────────────────────────────────────────────────────
  const cnvRef = useRef(null); // canvas element for print (was this.refs.cnv)
  const autoSaveIntervalRef = useRef(null); // autosave timer ID
  const prevPathNameRef = useRef(undefined);
  const signatureRequestDataRef = useRef([]);
  const sessionidRef = useRef(null);

  // loadEstimatorStateRef — kept current for socketCallbacksRef getState()
  const loadEstimatorStateRef = useRef(loadEstimatorState);
  loadEstimatorStateRef.current = loadEstimatorState;

  // propsRef — kept current so one-time useEffect closures always read latest props
  const propsRef = useRef(props);
  propsRef.current = props;

  const priorYearNoticeForThisReturn =
    props.priorYearNotice && String(props.priorYearNotice.returnID) === String(props.returnID)
      ? props.priorYearNotice
      : null;

  // Refs for componentDidUpdate "previous value" comparisons
  const prevMessageBadgeCountRef = useRef(props.messageBadgeCount);
  const prevWizardCompletedRef = useRef(props.wizardCompleted);
  const prevActiveErrorRejectTabRef = useRef(props.activeErrorRejectTab);

  // ─── Convenience accessor (replaces the class getter) ────────────────────
  const calcAPI = props.calcAPIRef?.current ?? null;

  // ─── One-time setup — fire once at mount ─────────────────────────────────
  useEffect(() => {
    propsRef.current.returnInitializedRef.current = false;
    XlinkAPI.canEditReturn().catch(() => {
      propsRef.current.lockReturnByPermissions();
    });
  }, []);

  /** Handles closing an active return a user is viewing. */
  const onCloseActiveReturn = useCallback(() => {
    const finishUp = async () => {
      props.setActiveReturnFormList();
    };

    props.resetRMDRequests();
    props.resetRemoteSign();
    props.closeReturnProfile();
    props.clearActiveReturn();
    props.closeActiveReturn();

    // Close reject modal if it is open
    if (props.errorRejectModalOpen) {
      props.closeErrorRejectModal();
      props.verify.setVerifyType('');
      props.verify.resetVerify();
    }

    // Allows to go to another page.
    props.miscState.setHasConfirmedClose(true);
    // Brings the user back to Tax Returns
    props.history.push({ pathname: '/tax-returns' });

    props.setReturnOpen(false);

    finishUp();
  }, [props]);

  /**
   * Handles closing the Preview PDF Modal is the close button is clicked
   *
   * @function
   */
  const formListReady = useCallback(() => {
    return props.formList !== undefined && props.formList.length > 0;
  }, [props.formList]);

  /**
   * Determine return locked message based on locked by user,
   * locked via access level, or locked status.
   */
  const getLockedMessage = useCallback(() => {
    let msg;
    let lockedUser = '';

    if (isSuperOrTechUserReadOnly(payload)) {
      msg = 'Super and tech support users are restricted from editing the return';
    } else if (props.isReadOnly) {
      msg =
        'This return is currently read-only mode. Possible reasons include limited access permissions, logging in under the wrong hierarchy, or the return already being submitted for review.';
    } else if (props.lockedBy) {
      msg = 'Return is being edited by ';
      lockedUser = <span>{props.lockedBy.toUpperCase()}</span>;
    } else if (props.officeProfile.is_feeder_office && props.queueReturn.readyForReview) {
      msg = 'Return has been submitted for review';
    } else if (props.lockedViaAccessLevel) {
      msg = 'Access level does not permit editing returns';
    } else if (props.lockedStatus) {
      msg = 'Return is in read-only mode and has been locked';
    }

    return (
      <div style={{ paddingTop: '0.4em', maxWidth: '90%' }}>
        <div className="lockedMsgBody">
          {'NOTICE: ' + msg} {lockedUser}
        </div>
      </div>
    );
  }, [
    payload,
    props.isReadOnly,
    props.lockedBy,
    props.officeProfile,
    props.queueReturn.readyForReview,
    props.lockedViaAccessLevel,
    props.lockedStatus,
  ]);

  // checks to if return has a filing status entered on the CDS screen, if not notify user
  const hasFLST = useCallback(() => {
    const filingStatus = props.clientFilingStatus || props?.wizardHasFLST;
    if ((!filingStatus || filingStatus === '0') && !payload.is_business) {
      return false;
    }
    return true;
  }, [payload, props.clientFilingStatus, props.wizardHasFLST]);

  const handleRequireFilingStatusDlg = useCallback(() => {
    props.miscState.openRequireFilingStatusDlg();
  }, [props.miscState]);

  // newSocketInit removed — socket lifecycle now owned by useCalcSession in FormViewerWrapper.

  const handleNewTextMessageNotification = useCallback(
    async (prevUnreadTextMessageCount = 0) => {
      if (props.messageBadgeCount > 0) {
        try {
          const res = await XlinkAPI.getReturnMessagesByReturnID(props.returnID);
          if (statusOK(res) && res.data.length > 0) {
            const { data } = res;
            const newCount = data.filter(m => m.is_read === false).length;

            setUnreadTextMessageCount(newCount);

            data.forEach(val => {
              val.date = moment(new Date(val.date)).format('M/DD/YYYY h:mm a');
            });
            props.updateMessageList(data);
            if (
              newCount > 0 &&
              !props.textLinkReceivedModalOpen &&
              newCount > prevUnreadTextMessageCount
            ) {
              props.showSnackbarMessage(
                `New TextLink Message${newCount > 1 ? 's' : ''}`,
                'success',
              );
            }
          }
        } catch (e) {
          props.showErrorDialog('Error fetching messages list');
        }
      }
    },
    [props],
  );

  // handles the 'PDWX' global — prompt shown once, then 'PDWX' set to true in DB
  const handlePrintPDFWarningMessage = useCallback(async () => {
    const printPDFWarningMessage = {
      configID: props.printPDFWarningMessageID,
      configValue: '1',
    };
    const submitData = [printPDFWarningMessage];

    try {
      await XlinkAPI.updateDefaultsSetup(submitData, props.loginID);
      store.dispatch(defaultsActions.setPrintPDFWarningMessage(true));
    } catch (err) {
      props.showErrorDialog('Error Updating Default Setting');
    }
  }, [props]);

  // ─── Mount / unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    // ── Register callbacks with FormViewerWrapper ─────────────────────────
    // onMessageRef is populated by useSocketMessage (see socketCallbacksRef below).
    if (props.onOpenRef) {
      props.onOpenRef.current = handleSessionOpen;
    }
    if (props.onCloseRef) {
      props.onCloseRef.current = onCloseActiveReturn;
    }
    if (props.navCallbacksRef) {
      props.navCallbacksRef.current = {
        onFormRendered: (formName, fileName, subFrm) => {
          propsRef.current.fieldEvents.subFrmRef.current = subFrm;
          propsRef.current.fieldEvents.maxTableRowRef.current = 0;
          propsRef.current.fieldEvents.bindFormEvents();
        },
        onToggleGroup: (e, n, evt) => propsRef.current.formPopulation.toggleGroup(e, n, evt),
        onInterviewStart: interviewMode,
        onRequireFilingStatus: () => propsRef.current.miscState.openRequireFilingStatusDlg(),
        onVerifyReturn: tab => propsRef.current.verify?.verifyReturn(tab),
        onLoadVerifyForm: (name, file) => propsRef.current.verify?.loadVerifyForm(name, file),
        onResetVerify: () => propsRef.current.verify?.resetVerify(),
        onSendRemotePayment: (...a) => propsRef.current.wallet.SendRemotePayment(...a),
        onStartSpin: msg => propsRef.current.spinner.startSpin(msg),
      };
    }

    if (props.formPopCallbacksRef) {
      props.formPopCallbacksRef.current = {
        onShowErrorSnackbar: msg => propsRef.current.snackbars.showErrorSnackbar(msg),
      };
    }

    if (props.verifyCallbacksRef) {
      props.verifyCallbacksRef.current = {
        onStartSpin: msg => propsRef.current.spinner.startSpin(msg),
        onShowErrorSnackbar: msg => propsRef.current.snackbars.showErrorSnackbar(msg),
        onCloseErrorRejectModal: () => propsRef.current.closeErrorRejectModal?.(),
        onViewAsset: id => propsRef.current.depreciation.viewAsset(id),
        onPerformCalcProcess: (...a) => propsRef.current.calcProcess.performCalcProcess(...a),
        onFormatIDForJquery: id => propsRef.current.fieldEvents?.formatIDForJquery(id),
        onUnbindFormEvents: () => propsRef.current.fieldEvents?.unbindFormEvents(),
        onBindFormEvents: () => propsRef.current.fieldEvents?.bindFormEvents(),
      };
    }

    if (props.printCallbacksRef) {
      props.printCallbacksRef.current = {
        onStartSpin: msg => propsRef.current.spinner.startSpin(msg),
        onStopSpin: () => propsRef.current.spinner.stopSpin(),
        onShowPreview: attachment => propsRef.current.documentAttach.showPreview(attachment),
        onGetCanvas: () => cnvRef.current,
        onSaveReturn: () => propsRef.current.calcAPIRef?.current?.ReturnSave?.(),
      };
    }

    if (props.fieldEventsCallbacksRef) {
      props.fieldEventsCallbacksRef.current = {
        onPerformCalcProcess: (...a) => propsRef.current.calcProcess.performCalcProcess(...a),
        onOpenChoiceList: (...a) => propsRef.current.choiceList.openChoiceList(...a),
        onCheckChoiceListForValue: el => propsRef.current.choiceList.checkChoiceListForValue(el),
        onHandleChoiceListItem: (...a) => propsRef.current.choiceList.handleChoiceListItem(...a),
        onIsPackageAvailable: val => propsRef.current.print.isPackageAvailable(val),
        onShowErrorSnackbar: msg => propsRef.current.snackbars.showErrorSnackbar(msg),
        onShowPromptSnackbar: msg => propsRef.current.snackbars.showPromptSnackbar(msg),
        onHidePromptSnackbar: () => propsRef.current.snackbars.hidePromptSnackbar(),
        onSetBookmarkFldID: id => propsRef.current.bookmarks.setBookmarkFldID(id),
        onSetFieldFormLinks: links => propsRef.current.miscState.setFieldFormLinks(links),
      };
    }

    if (props.returnCloseCallbacksRef) {
      props.returnCloseCallbacksRef.current = {
        onHasFLST: () => {
          const filingStatus =
            propsRef.current.clientFilingStatus || propsRef.current.wizardHasFLST;
          if ((!filingStatus || filingStatus === '0') && !payload?.is_business) return false;
          return true;
        },
        onHandleRequireFilingStatusDlg: () => handleRequireFilingStatusDlg(),
        onCloseActiveReturn: () => onCloseActiveReturn(),
        onStartSpin: msg => propsRef.current.spinner.startSpin(msg),
        onShowErrorSnackbar: msg => propsRef.current.snackbars.showErrorSnackbar(msg),
        getFormList: () => propsRef.current.formList,
        getSeason: () => payload?.season,
        getIsWizard: () => propsRef.current.isWizard,
        getIsReadOnly: () => propsRef.current.isReadOnly,
        getReturnInitialized: () => propsRef.current.returnInitializedRef?.current,
        getSilentlySaveReturnOnClose: () => propsRef.current.silentlySaveReturnOnClose,
      };
    }

    // Register comprehensive env for useSocketMessage.
    if (props.socketCallbacksRef) {
      props.socketCallbacksRef.current = {
        // hook objects — read via propsRef so they stay current
        get spinner() {
          return propsRef.current.spinner;
        },
        get snackbars() {
          return propsRef.current.snackbars;
        },
        get formNav() {
          return propsRef.current.formNav;
        },
        get fieldEvents() {
          return propsRef.current.fieldEvents;
        },
        get formPopulation() {
          return propsRef.current.formPopulation;
        },
        get verify() {
          return propsRef.current.verify;
        },
        get print() {
          return propsRef.current.print;
        },
        get bookmarks() {
          return propsRef.current.bookmarks;
        },
        get returnClose() {
          return propsRef.current.returnClose;
        },
        showCalcError: (title, msg) => propsRef.current.calcError.showCalcError(title, msg),
        // TVA setters
        setIsVisionModalOpen: v => propsRef.current.tva.setIsVisionModalOpen(v),
        setTVAprogress: v => propsRef.current.tva.setTVAprogress(v),
        setTVARetrieveCount: v => propsRef.current.tva.setTVARetrieveCount(v),
        setOpenTVADisallow: v => propsRef.current.tva.setOpenTVADisallow(v),
        setTVAargType: v => propsRef.current.tva.setTVAargType(v),
        setTvaObject: v => propsRef.current.tva.setTvaObject(v),
        setTvaID: v => propsRef.current.tva.setTvaID(v),
        getTVARetrieveCount: () => propsRef.current.tva.TVARetrieveCount,
        setK1List: v => propsRef.current.k1Manager.setK1List(v),
        setK1pkgList: v => propsRef.current.k1Manager.setK1pkgList(v),
        setHideK1Flag: v => propsRef.current.k1Manager.setHideK1Flag(v),
        // state bridge — replaces this.setState / { loadEstimatorState: loadEstimatorStateRef.current }
        setState: (partialState, fn1, fn2) => {
          fn1?.();
          fn2?.();
          if (partialState?.loadEstimatorState !== undefined) {
            setLoadEstimatorState(partialState.loadEstimatorState);
          }
        },
        getState: () => ({ loadEstimatorState: loadEstimatorStateRef.current }),
        setSessionId: id => {
          sessionidRef.current = id;
        },
        // methods
        lockFieldsIfRequired: () => propsRef.current.returnLock.lockFieldsIfRequired(),
        handleMenuCommand: (...a) => propsRef.current.menuCommand.handleMenuCommand(...a),
        handleSpecialFormLoadResponse: d =>
          propsRef.current.specialFormLoad.handleSpecialFormLoadResponse(d),
        interviewMode: () => interviewMode(),
        setVerifyType: t => propsRef.current.verify.setVerifyType(t),
        getLatestArchivedDocuments: () =>
          propsRef.current.documentAttach.getLatestArchivedDocuments(),
        openAddAttachmentModal: () => propsRef.current.documentAttach.openAddAttachmentModal(),
        closeQueueReturnModal: () => propsRef.current.queueReturn.closeQueueReturnModal(),
        openQueueReturnModal: () => propsRef.current.queueReturn.openQueueReturnModal(),
        setQueueData: d => propsRef.current.queueReturn.setQueueData(d),
        getReadyForReview: () => propsRef.current.queueReturn.readyForReview,
        PopulateFormsAndActivitiesList: l =>
          propsRef.current.addForm.populateFormsAndActivitiesList(l),
        toggleAssetSnackbar: (m, t) => propsRef.current.depreciation.toggleAssetSnackbar(m, t),
        setReassignAssetList: v => propsRef.current.depreciation.setReassignAssetList(v),
        setPaymentTerminalStatus: s => propsRef.current.paymentCapture.setPaymentTerminalStatus(s),
        setReturnPaymentInfo: v => propsRef.current.paymentCapture.setReturnPaymentInfo(v),
        toggleFieldLock: v => propsRef.current.returnLock.toggleFieldLock(v),
        handleQueueReturn: () => propsRef.current.queueReturn.handleQueueReturn(),
        handlePrintFinal: () => handlePrintFinal(),
        onUpdateFormError: r => propsRef.current.formErrors.onUpdateFormError(r),
        handleFilingStatusError: e => propsRef.current.formErrors.handleFilingStatusError(e),
        copyToTraining: () => propsRef.current.copyReturn.copyToTraining(),
        copyToLive: () => propsRef.current.copyReturn.copyToLive(),
        setDupeSSNCopyRtnOpen: v => propsRef.current.copyReturn.setDupeSSNCopyRtnOpen(v),
        setDupeSSNCopyLiveRtnOpen: v => propsRef.current.copyReturn.setDupeSSNCopyLiveRtnOpen(v),
        setShowTextLinkSendErrorDialog: v =>
          propsRef.current.textLink.setShowTextLinkSendErrorDialog(v),
        setTextlinkErrorMessage: v => propsRef.current.textLink.setTextlinkErrorMessage(v),
        updateRestrictedFields: a => propsRef.current.restrictedFields.updateRestrictedFields(a),
        restorePreviousValue: () => propsRef.current.formErrors.restorePreviousValue(),
        clearEstimatorState: () => propsRef.current.clearEstimatorState?.(),
        // Redux props — read at call-time via propsRef so they stay current
        get signatureRequestData() {
          return signatureRequestDataRef.current;
        },
        get currentElRef() {
          return propsRef.current.currentElRef;
        },
        get returnInitializedRef() {
          return propsRef.current.returnInitializedRef;
        },
        get returnID() {
          return propsRef.current.returnID;
        },
        get isSelfPreparing() {
          return propsRef.current.isSelfPreparing;
        },
        get isSignatureRequestActive() {
          return propsRef.current.isSignatureRequestActive;
        },
        get isRMSSigningMethod() {
          return propsRef.current.isRMSSigningMethod;
        },
        get encryptedPdfSelected() {
          return propsRef.current.encryptedPdfSelected;
        },
        get taxpayerChecked() {
          return propsRef.current.taxpayerChecked;
        },
        get taxpayerEPasshecked() {
          return propsRef.current.taxpayerEPasshecked;
        },
        get spouseChecked() {
          return propsRef.current.spouseChecked;
        },
        get spouseEPassChecked() {
          return propsRef.current.spouseEPassChecked;
        },
        get startInterview() {
          return propsRef.current.startInterview;
        },
        get addFormPkg() {
          return propsRef.current.addFormPkg;
        },
        get lockedStatus() {
          return propsRef.current.lockedStatus;
        },
        get payload() {
          return payload;
        },
        get eroData() {
          return propsRef.current.eroData;
        },
        get preparerData() {
          return propsRef.current.preparerData;
        },
        get amendedPreparerData() {
          return propsRef.current.amendedPreparerData;
        },
        get initialSigneeData() {
          return propsRef.current.initialSigneeData;
        },
        get primarySSNEIN() {
          return propsRef.current.primarySSNEIN;
        },
        // Redux action dispatchers
        needingVerifiedSet: v => propsRef.current.needingVerifiedSet(v),
        openErrorRejectModal: () => propsRef.current.openErrorRejectModal?.(),
        toggleVerifyModalDialog: v => propsRef.current.toggleVerifyModalDialog(v),
        toggleVerifyDialog: v => propsRef.current.toggleVerifyDialog(v),
        openMsgDialog: m => propsRef.current.openMsgDialog(m),
        showErrorDialog: m => propsRef.current.showErrorDialog(m),
        showSnackbarMessage: (...a) => propsRef.current.showSnackbarMessage(...a),
        setIsSelfPreparingIndicator: v => propsRef.current.setIsSelfPreparingIndicator(v),
        signaturesNeededSet: v => propsRef.current.signaturesNeededSet(v),
        bankAppAttachedSet: v => propsRef.current.bankAppAttachedSet(v),
        setPreparerID: v => propsRef.current.setPreparerID(v),
        setAmendedPreparerID: v => propsRef.current.setAmendedPreparerID(v),
        setSuggestedPassword: v => propsRef.current.setSuggestedPassword(v),
        togglePrintDialog: v => propsRef.current.togglePrintDialog(v),
        toggleSuggestedPassword: v => propsRef.current.toggleSuggestedPassword(v),
        toggleEncryptedPdf: v => propsRef.current.toggleEncryptedPdf(v),
        setEncryptedPdfRequest: v => propsRef.current.setEncryptedPdfRequest(v),
        setEncryptedDocumentContent: v => propsRef.current.setEncryptedDocumentContent(v),
        setEmailBlob: v => propsRef.current.setEmailBlob(v),
        setRMSflag: v => propsRef.current.setRMSflag(v),
        inOfficeSignSuccessfulSet: v => propsRef.current.inOfficeSignSuccessfulSet(v),
        setStateAcks: v => propsRef.current.setStateAcks(v),
        setAttachedStates: v => propsRef.current.setAttachedStates(v),
        removeDocument: v => propsRef.current.removeDocument(v),
        setSentSuccessTitle: v => propsRef.current.setSentSuccessTitle(v),
        setSentSuccessMsg: v => propsRef.current.setSentSuccessMsg(v),
        setEncryptedPDFSuccessDialog: v => propsRef.current.setEncryptedPDFSuccessDialog(v),
        toggleTextLinkSendModalOriginal: (...a) =>
          propsRef.current.toggleTextLinkSendModalOriginal(...a),
        // useImportStock
        setImportStockMsg: v => propsRef.current.importStock.setImportStockMsg(v),
        setBtnClickSpinner: v => propsRef.current.importStock.setBtnClickSpinner(v),
        closeImportStock: () => propsRef.current.importStock.closeImportStock(),
        // useDocumentAttach
        getPreparerID: () => propsRef.current.documentAttach.preparerID,
        // useMiscState
        setReturnSaved: v => propsRef.current.miscState.setReturnSaved(v),
        setBankID: v => propsRef.current.miscState.setBankID(v),
        getBankID: () => propsRef.current.miscState.bankID,
        setFieldFormLinks: v => propsRef.current.miscState.setFieldFormLinks(v),
        setRestrictedFieldData: v => propsRef.current.miscState.setRestrictedFieldData(v),
      };
    }

    handleNewTextMessageNotification();
    // initially sets email subject title for encrypted pdfs depending on the tax year of the return
    props.setEncryptedPdfTitle(props.taxYear + ' TAX RETURN');
    window.location.pathname === '/refund-calculator' &&
      (prevPathNameRef.current = window.location.pathname);
    history.pushState(null, null, location.pathname);

    props.setReturnOpen(true);

    let demoAccountFlag = false;
    const accountCode = payload.account_code;
    if (accountCode === 'CLODMO') {
      demoAccountFlag = true;
    }
    props.setAccountType(demoAccountFlag);
    props.setReadyForReviewFilter(false);
    // Do not allow user to close window abruptly
    window.addEventListener('beforeunload', handleSuddenCloseWindow);
    // Inject bootstrap script
    const bootstrapJquery = document.createElement('script');
    bootstrapJquery.src = 'https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/js/bootstrap.min.js';
    bootstrapJquery.integrity =
      'sha384-Tc5IQib027qvyjSMfHjOMaLkfuWVxZxUPnCJA7l2mCWNIpG9mGCD8wGNIcPD7Txa';
    bootstrapJquery.crossOrigin = 'anonymous';
    bootstrapJquery.id = 'btstrapJquerySrc';
    document.head.appendChild(bootstrapJquery);

    // Hide button element on mount; listen for window resize to reposition it.
    const el = document.getElementById('buttonMenuForReturns');
    el.style.display = 'none';
    const resizeHandler = props.fieldEvents.renderButtonChoice;
    window.addEventListener('resize', resizeHandler);

    if (
      props.currentView.role === HIERARCHY_TYPE.EFIN ||
      props.currentView.role === HIERARCHY_TYPE.PREPARER
    ) {
      XlinkAPI.isTextLinkActivated()
        .then(res => {
          propsRef.current.setTextLinkActivationStatus(res.data.isConfigured);
        })
        .catch(() => {
          propsRef.current.setTextLinkActivationStatus(false);
        });
    }

    // get header bar for Guided Estimator
    if (props?.isWizard && props?.setFormViewerHeader) {
      props.setFormViewerHeader(renderHeaderBar());
    }

    let priorYearNoticeCancelled = false;

    if (!priorYearNoticeForThisReturn) {
      lockReturnWithPriorYearNotice(props.returnID)
        .then(({ notice }) => {
          if (priorYearNoticeCancelled) return;
          if (notice?.mode) {
            props.setPriorYearNotice({ ...notice, returnID: props.returnID });
          }
        })
        .catch(err => {
          console.log(
            'CRITICAL: failed to lock return, continuing anyway but this needs to be handled',
          );
          console.log(err);
        });
    }

    return () => {
      // ── componentWillUnmount cleanup ──────────────────────────────────────
      priorYearNoticeCancelled = true;
      prevPathNameRef.current === '/refund-calculator' && window.history.back();

      window.removeEventListener('resize', resizeHandler);
      clearInterval(autoSaveIntervalRef.current);
      document.body.classList.remove('bodyStyleOverflowHidden');
      propsRef.current.removeFrontEndNotification();
      propsRef.current.setStatePackageSelected('');
      window.removeEventListener('beforeunload', handleSuddenCloseWindow);
      propsRef.current.setAccountType(false);
      document.getElementById('taxprepJS')?.remove();
      document.getElementById('btstrapJquerySrc')?.remove();
      if (sessionStorage.getItem('formViewerCompLoaded')) {
        sessionStorage.setItem('formViewerCompLoaded', false);
      }
      propsRef.current.setUpdatedAttachmentList([]);
      // Prior Year Fee notice (CORP-1199). The notice is scoped to the return
      // being opened and carries no return ID, so leaving it in the store would
      // let it be mistaken for the next return's notice, which the mount-time
      // lock guard reads to decide whether to lock at all.
      propsRef.current.setPriorYearNotice(null);
      propsRef.current.signaturesNeededSet('');
      propsRef.current.setPreparerID('');
      propsRef.current.setAmendedPreparerID('');
      propsRef.current.closeYTYDialog();
    };
  }, []);

  // When message badge count increments, check for new messages for the open return.
  useEffect(() => {
    const prev = prevMessageBadgeCountRef.current;
    prevMessageBadgeCountRef.current = props.messageBadgeCount;
    if (props.messageBadgeCount > prev) {
      handleNewTextMessageNotification(unreadTextMessageCount);
    }
  }, [props.messageBadgeCount]);

  // When wizard is marked complete, re-render the active form.
  useEffect(() => {
    const prev = prevWizardCompletedRef.current;
    prevWizardCompletedRef.current = props.wizardCompleted;
    if (props.returnInitializedRef.current && !prev && props.wizardCompleted) {
      props.formNav.getTaxForm(props.formNav.activeForm, '', '', '');
    }
  }, [props.wizardCompleted]);

  // When activeErrorRejectTab transitions from null, sync to verify hook.
  useEffect(() => {
    const prev = prevActiveErrorRejectTabRef.current;
    prevActiveErrorRejectTabRef.current = props.activeErrorRejectTab;
    if (prev === null && props.activeErrorRejectTab !== null) {
      props.verify?.setActiveErrorRejectTab(props.activeErrorRejectTab);
    }
  }, [props.activeErrorRejectTab]);

  // Keep bookmarkFieldRef in sync with the latest bookmark field value.
  useEffect(() => {
    if (props.bookmarkFieldRef) {
      props.bookmarkFieldRef.current = props.bookmarks.bookmarkField;
    }
  }, [props.bookmarks.bookmarkField, props.bookmarkFieldRef]);

  const interviewMode = useCallback(() => {
    props.calcAPIRef?.current?.ReturnFormLoad('Z00501', '', '', '');
    // interviewMode state is now owned by useFormNavigation (props.formNav); no setState needed here.
  }, [props.calcAPIRef]);

  /**
   * Called by useCalcSession (via onOpenRef) when SOCKET_OPEN is received.
   * Sends ReturnInit on the first connection, then sets up auto-save.
   * On reconnects (returnInitializedRef.current === true) this is a no-op.
   */
  const handleSessionOpen = useCallback(async () => {
    if (!propsRef.current.returnInitializedRef.current) {
      // We only want to send return_init once. Check if the return has been initialized.

      // emulate CalcMutator's returnInit, since we're not using it in FormViewer yet
      // let blob = sessionStorage.getItem("ESTIMATOR_BLOB")
      const blob = propsRef.current.estimatorBlob;
      if (blob && blob !== null) {
        setLoadEstimatorState(true);
      }
      await propsRef.current.calcAPIRef?.current?.ReturnInit(propsRef.current.loginID, false, blob);

      let activeOfficeID = 0;
      if (payload.hierarchy_type_id === HIERARCHY_TYPE.PREPARER) {
        activeOfficeID = payload.preparer_info.active_office_id;
      } else {
        activeOfficeID = await UtilityAPI.getLastDrilledOfficeID(propsRef.current.ddh);
      }

      propsRef.current.print.getPrintingPrefs();
      propsRef.current.print.getAvailablePackages();
      // Set active efin for remote signature feature
      propsRef.current.setActiveEfin(activeOfficeID);

      // Silently save return
      // Every 120 seconds when no autoSaveAfterMins value is set under the setup defaults page
      // Every x seconds when autoSaveAfterMins value is set under the setup defaults page
      if (typeof payload !== 'undefined') {
        // Autosave is disabled when:
        // * The Return is locked
        // * The Return is being edited by an admin or tech support login
        // * The Return is being edited by an admin or tech support while impersonating
        if (
          !propsRef.current.isLockedFn() &&
          (payload.hierarchy_type_id !== HIERARCHY_TYPE.ADMIN ||
            payload.hierarchy_type_id !== HIERARCHY_TYPE.TECH_SUPPORT ||
            payload.hierarchy_type_id !== HIERARCHY_TYPE.TECH_SUPPORT_SBA) &&
          !payload.is_tech_support_and_impersonating &&
          !payload.is_super_user
        ) {
          let autoSaveAfterMilisec = 0;
          if (
            propsRef.current.autoSaveAfterMins === '' ||
            propsRef.current.autoSaveAfterMins === '0'
          ) {
            autoSaveAfterMilisec = AutoSaveInterval;
          } else {
            autoSaveAfterMilisec = propsRef.current.autoSaveAfterMins * 60000; // Converting time to milliseconds
          }
          autoSaveIntervalRef.current = setInterval(() => {
            // Read all props fresh via propsRef to avoid stale closure
            const filingStatus =
              propsRef.current.clientFilingStatus || propsRef.current.wizardHasFLST;
            const hasFiling = (filingStatus && filingStatus !== '0') || payload.is_business;
            if (
              !hasFiling ||
              assetFormRegExp.test(propsRef.current.formNav.activeForm) ||
              propsRef.current.autoSaveEnabled === '0'
            ) {
              // Do not save if filing status has not been set or if currently on an asset form
            } else {
              try {
                propsRef.current.calcAPIRef?.current?.TriggerAutoSave(propsRef.current.isReadOnly);
              } catch (error) {
                ErrorHelpers.handleError('Error saving return', error);
              }
            }
          }, autoSaveAfterMilisec);
        }
      }
    }
  }, [payload, propsRef]);

  // TODO -- Pass this down to HeaderBarForms and only handle verify setting in FormViewer?
  const handlePrintFinal = useCallback(() => {
    props.verify.setVerifyType('printFinal');
    props.verify.verifyReturn(VERIFY_MODAL_TABS.VERIFY);
  }, [props.verify]);

  const handleSignReturn = useCallback(() => {
    props.verify.setVerifyType('signReturn');
    props.verify.verifyReturn(VERIFY_MODAL_TABS.VERIFY);
  }, [props.verify]);

  const handleRequestDocs = useCallback(() => {
    props.verify.setVerifyType('requestDocs');
    props.verify.verifyReturn(VERIFY_MODAL_TABS.VERIFY);
  }, [props.verify]);

  // Handles sorting and saving the calc response or an updated set of notes to local state.

  const loadSpecialForm = useCallback(() => {
    if (props.formNav.activeForm.length >= 7) {
      // Worksheet/Overflow/Statement Form, populate slider instead
      return (
        <Slide direction="up" in={props.formNav.activeForm.length >= 7} mountOnEnter unmountOnExit>
          <SubformViewer
            viewAsset={props.depreciation.viewAsset}
            getTaxForm={props.formNav.getTaxForm}
            activeForm={
              props.formNav.activeForm.slice(0, 3) === 'ovl'
                ? props.formNav.formStack[
                    props.formNav.formStack.length >= 1 ? props.formNav.formStack.length - 1 : 0
                  ].form
                : props.formNav.activeForm
            }
            activeFile={props.formNav.activeFile}
            // fromField keeps track of the field formload is called from
            // Used when calling getTaxForm from a nested worksheet/overflow/statement
            fromField={
              props.formNav.formStack.length >= 2
                ? props.formNav.formStack[props.formNav.formStack.length - 2].field
                : ''
            }
          />
        </Slide>
      );
    }
    switch (props.formNav.activeForm) {
      case 'retn24':
        return (
          <ArchivedDocuments
            printMeta={printMeta}
            getDocumentTypeList={undefined}
            openDocumentAttachModal={undefined}
            attachments={props.attachments}
            detachDocument={props.documentAttach.onClickDetachDocument}
            closeAddDocumentModalOk={props.pdfAttach.addOrAttachDocument}
            showSnackBarErr={msg => props.snackbars.showErrorSnackbar(msg)}
            locked={props.isLockedFn()}
            returnID={parseInt(props.returnID)}
            newestAttachments={props.attachmentsListMostRecent}
            includeCompletedRS={props.documentAttach.getLatestArchivedDocuments}
            updatedDocList={props.setUpdatedAttachmentList}
            verifyReturn={() => handleRequestDocs()}
            toggleRequestDocsModal={props.documentAttach.toggleRequestDocsModal}
            isRequestDocumentModalOpen={props.documentAttach.isRequestDocumentModalOpen}
            formList={props.formList}
            readyForReview={props.queueReturn.readyForReview}
            isFeederOffice={props.officeProfile.is_feeder_office}
            isBusiness={payload?.is_business}
            saveReturn={() => props.returnClose.saveReturn()}
            getWalletToken={props.wallet.getWalletToken}
            removeDocument={props.documentAttach.onClickDeleteDocument}
          />
        );
      case 'prin12':
      case 'prin13':
      case 'prin17':
      case 'prin18':
        return (
          <Signatures
            efinNumber={props.print.print.efinSignature}
            printPrefs={props.print.print?.prefs}
            bankID={props.miscState.bankID}
          />
        );
      case 'form12':
        return (
          <AssetList
            updateActivities={pkg => props.addForm.updateAddFormList(pkg)}
            openAddFormList={(tab, index) => openAddFormList(tab, index)}
            viewAsset={id => props.depreciation.viewAsset(id)}
            deleteAsset={id => props.depreciation.deleteAsset(id)}
            fetchReassignAssetList={id => calcAPI.ReturnReassignAssetList(id)}
            reassignAssetList={props.depreciation.reassignAssetList}
            reassignAsset={(astID, actvID) => props.depreciation.reassignAsset(astID, actvID)}
            toggleSnackbar={(message, toggle) =>
              props.depreciation.toggleAssetSnackbar(message, toggle)
            }
            activityList={props.activityList}
            showSnackbar={props.depreciation.assetSnackbar}
            snackbarMessage={props.depreciation.assetSnackbarMsg}
            calcAPI={calcAPI}
            locked={props.isLockedFn()}
            tabListDefinitions={tabListDefinitions}
            readyForReview={props.queueReturn.readyForReview}
            isFeederOffice={props.officeProfile.is_feeder_office}
          />
        );
      case 'retn18':
        return (
          <Bookmarks
            bookmarks={props.bookmarks.bookmarks.bookmarkData}
            setBookmark={(a, b, c, d, e) => calcAPI.ReturnBookmarkUpsert(a, b, c, d, e)}
            viewBookmark={(a, b) => calcAPI.ReturnBookmarkQuery(a, b)}
            showErrorDialog={props.showErrorDialog}
            getTaxForm={props.formNav.getTaxForm}
            deleteBookmark={props.bookmarks.deleteBookmark}
            snackbarToggle={msg => props.snackbars.showPromptSnackbar(msg)}
            toggleFromBookmark={(toggle, field) =>
              props.bookmarks.toggleFromBookmark(toggle, field)
            }
            locked={payload.is_tech_support_and_impersonating}
          />
        );
      case 'retn19':
        return (
          <ReturnNotes
            notes={props.bookmarks.notes}
            printMeta={printMeta}
            noteUpsert={props.bookmarks.handleNoteUpsert}
            locked={payload.is_tech_support_and_impersonating}
            readyForReview={props.queueReturn.readyForReview}
            isFeederOffice={props.officeProfile.is_feeder_office}
          />
        );
      default:
        return <div id="taxform" />;
    }
  }, [props, payload, printMeta, calcAPI, handleRequestDocs, handleSignReturn]);

  /**
   * If user clicks "Add New Documents" Btn, the AddDocumentModal opens.
   */

  const openAddFormList = useCallback(
    (tab, index) => {
      if (!hasFLST()) {
        handleRequireFilingStatusDlg();
      } else {
        props.addForm.setActiveAddFormTab(index);
        if (tab.label === 'Depreciation') {
          props.setAddFormPackage('BA');
          props.setFirstAddForm(true);
          calcAPI.ReturnFormList('BA', props.isReadOnly);
        } else {
          // Setting the package that is being used for getting the forms list
          props.setAddFormPackage('US');
          // Need flag to know if the function calcAPI.ReturnFormList is being called for the first time, which means we need to open the add form modal
          props.setFirstAddForm(true);
          // Getting forms list from calc server
          calcAPI.ReturnFormList('US', props.isReadOnly);
        }
      }
    },
    [hasFLST, handleRequireFilingStatusDlg, props, calcAPI],
  );

  const closeErrorRejectModal = useCallback(() => {
    props.closeErrorRejectModal();
    props.verify.setVerifyType('');
    props.verify.resetVerify();
  }, [props]);

  const handleAddForm = useCallback(
    form => {
      if (!hasFLST()) {
        handleRequireFilingStatusDlg();
      } else {
        if (typeof form.MSGBOX === 'undefined') {
          // if we are in the subMenu do not re-set the TORS of an existing form to 00.
          // set Form.TORS to null to prevent override.
          if (props.setSubDialogListOpened && form.TORS === '00') {
            form.TORS = null;
          }
          calcAPI.ReturnAddForm(form.formOccur, form.TORS);
          props.closeFormSubDialog();
          props.toggleAddFormList();
          props.formNav.getTaxForm(form.formOccur, '', '', '');
          return;
        }

        props.openMsgDialog(form.MSGBOX);
      }
    },
    [hasFLST, handleRequireFilingStatusDlg, props, calcAPI],
  );

  const handleAddFormIndex = useCallback(
    form => {
      if (!hasFLST()) {
        handleRequireFilingStatusDlg();
      } else {
        if (typeof form.MSGBOX === 'undefined') {
          calcAPI.ReturnAddForm(form.formOccur, form.TORS);
          props.toggleAddFormList();
          props.formNav.goToField(form.formOccur, form.formId.slice(4));
          return;
        }
        props.openMsgDialog(form.MSGBOX);
      }
    },
    [hasFLST, handleRequireFilingStatusDlg, props, calcAPI],
  );

  const handleResponseFromDialog = useCallback(
    (domEvent, resp, labelName) => {
      domEvent.preventDefault();
      calcAPI.ReturnMessageResponse(resp, props.formNav.activeForm, labelName, props.tva.tvaID);
      if (domEvent.target.innerText === 'My Wallet') {
        props.wallet.gotoWallet();
      }
      props.closeMsgDialog();
    },
    [calcAPI, props],
  );

  const handleBackButton = useCallback(
    (location, action) => {
      if (action === 'POP') {
        // wizard mode action for hitting back
        if (props?.isWizard) {
          props.closeReturnProfile();
          props.clearActiveReturn();
          props.closeActiveReturn();
          // allows push to go to tax returns page
          props.miscState.setHasConfirmedClose(true);
          props.history.push({ pathname: '/tax-returns' });
        } else {
          history.pushState(null, null, location.pathname);
          props.returnClose.setConfirmReturnClose(true);
        }
      }
    },
    [props],
  );

  /**
   * Persists the "Do not remind me again" preference (CORP-1199).
   *
   * This is the ordinary login setting, separate from the acknowledgment record.
   * It switches off the informational reminder across every prior season —
   * login_settings hangs off the login with no season dimension — and never
   * affects whether a mandatory acknowledgment is shown.
   *
   * Goes through the existing updateLoginSettings action rather than calling the
   * endpoint directly, so this saves the same way Login Preferences does. The
   * server replaces the stored blob wholesale, hence spreading the current
   * settings rather than sending a delta.
   *
   * Takes the value rather than hardcoding false, because the checkbox has to
   * work in both directions. It comes up checked for a preparer who already has
   * reminders off, so unchecking it has to be able to turn them back on.
   *
   * @param {boolean} remindersEnabled whether the reminder should be shown.
   */
  const persistPriorYearReminderPreference = remindersEnabled => {
    const settings = { ...(props.loginSettings || {}) };
    settings[LOGIN_SETTINGS.SHOW_PRIOR_YEAR_FEE_NOTICE] = {
      ...(settings[LOGIN_SETTINGS.SHOW_PRIOR_YEAR_FEE_NOTICE] || {
        text: 'Show Prior Year Fee notice',
      }),
      value: remindersEnabled,
    };
    props.setLoginSettings(settings);
    props.updateLoginSettings(settings);
  };

  /**
   * Saves the reminder preference only when the preparer actually changed it.
   *
   * Writing unconditionally would be destructive. When the server cannot read the
   * preference it reports reminders as enabled so the box renders unchecked, and
   * an unconditional save would then turn reminders back on for someone who had
   * switched them off and never touched the checkbox. Comparing against the value
   * the notice was rendered with means an untouched box writes nothing.
   *
   * @param {boolean} suppressReminders state of the checkbox at confirm time.
   */
  const savePriorYearReminderPreferenceIfChanged = suppressReminders => {
    if (priorYearNoticeForThisReturn?.canSetReminderPreference === false) return;

    const wasSuppressed = priorYearNoticeForThisReturn?.remindersEnabled === false;
    if (suppressReminders !== wasSuppressed) {
      persistPriorYearReminderPreference(!suppressReminders);
    }
  };

  const reconcilePriorYearNoticeWithServer = async () => {
    let notice;
    try {
      ({ notice } = await lockReturnWithPriorYearNotice(props.returnID));
    } catch (err) {
      console.log(
        'CRITICAL: failed to lock return, continuing anyway but this needs to be handled',
      );
      console.log(err);
      // No answer, so nothing is known. Leave the notice as it stands.
      return;
    }

    if (notice?.mode === PRIOR_YEAR_NOTICE_MODE.ACKNOWLEDGMENT) {
      props.setPriorYearNotice({ ...notice, returnID: props.returnID });
      return;
    }

    // The server answered and requires nothing further.
    props.setPriorYearNotice(null);
  };

  /**
   * Records the acknowledgment, then reconciles with the server either way.
   *
   * The record is written first and unconditionally; the reminder preference is
   * best-effort behind it, so a failed preference write can never cost us the
   * acknowledgment legal requires.
   */
  const handleAcknowledgePriorYearNotice = async (noticeVersionID, suppressReminders) => {
    try {
      await acknowledgePriorYearNotice(noticeVersionID, suppressReminders, () =>
        savePriorYearReminderPreferenceIfChanged(suppressReminders),
      );
    } catch (error) {
      ErrorHelpers.handleError('Unable to record Prior Year Fee acknowledgment', error);
    }

    await reconcilePriorYearNoticeWithServer();
  };

  /** Dismisses the informational reminder, saving the preference if it changed. */
  const handleDismissPriorYearNotice = suppressReminders => {
    savePriorYearReminderPreferenceIfChanged(suppressReminders);
    props.setPriorYearNotice(null);
  };

  const renderHeaderBar = useCallback(() => {
    return (
      <HeaderBarForms
        notesAlert={props.bookmarks.isNotesAlertOpen}
        assetMode={props.formNav.assetMode}
        taxYear={props.taxYear}
        clientName={props.clientName}
        deleteReturn={
          props.isLockedFn()
            ? () => props.returnClose.closeLockedReturn()
            : () => props.returnClose.deleteReturn()
        }
        showConfirmDelete={() => props.returnClose.showConfirmDeleteReturn()}
        closeAndSaveReturn={() => props.returnClose.closeAndSaveReturn()}
        closeLockedReturn={() => props.returnClose.closeLockedReturn()}
        closeReturn={() => props.returnClose.closeReturn()}
        showConfirmClose={() => props.returnClose.showConfirmClose()}
        saveReturn={() => props.returnClose.saveReturn()}
        setVerifyType={type => props.verify.setVerifyType(type)}
        verifyReturn={props.verify.verifyReturn}
        prefs={props.print.print.prefs}
        handlePrint={(type, prefs, formOccur, menuText) =>
          props.print.handlePrint(type, prefs, formOccur, menuText)
        }
        handleOpenTextLinkSendModal={props.print.handleOpenTextLinkSendModal}
        toggleTextLinkReceivedModal={props.toggleTextLinkReceivedModal}
        toggleShowNoTextLinkClientsDlg={props.toggleShowNoTextLinkClientsDlg}
        toggleTextLinkNotActivatedDialog={props.toggleTextLinkNotActivatedDialog}
        isTextLinkActivated={props.isTextLinkActivated}
        handlePrintSignatures={type => props.print.handleGetReqSigs(type)}
        openPrintReturnComponents={props.print.openPrintComponentsModal}
        getStateAckList={props.print.getStateAckList}
        getAmendedStateAckList={props.print.getAmendedStateAckList}
        getExtensionStateAckList={props.print.getExtensionStateAckList}
        getAttachedStates={props.print.getAttachedStateList}
        handleAttachedFormsPrint={props.print.handleAttachedFormsPrint}
        interviewMode={interviewMode}
        reloadBilling={props.submitForReview.reloadBilling}
        showYearToYear={props.transferData.openTransferDataOptionsModal}
        toggleYearToYearOptions={props.pyReturns?.length > 0}
        setReturnReviewStatusDialog={props.submitForReview.setReturnReviewStatusDialog}
        toggleReturnPayment={props.paymentCapture.toggleReturnPayment}
        toggleNotes={props.bookmarks.toggleNotesForm}
        toggleReturnStatusLock={props.returnLock.toggleReturnStatusLock}
        isTrainingMode={props.isTrainingMode}
        readyForReview={props.queueReturn.readyForReview}
        retractReturnForReview={props.submitForReview.handleConfirmSubmitForReview}
        copyToTraining={props.copyReturn.copyToTrainingDupeCheck}
        copyToLiveDupeCheck={props.copyReturn.copyToLiveDupeCheck}
        currentView={props.currentView}
        convertPrintPrefs={(prefs, printType) => props.print.convertPrintPrefs(prefs, printType)}
        season={payload?.season}
        usingDemoAccount={props.usingDemoAccount}
        handleNoPrintDemo={props.print.handleNoPrintDemo}
        inReviewByTransmitOffice={
          props.officeProfile &&
          !props.officeProfile.is_feeder_office &&
          props.queueReturn.readyForReview
        }
        ddh={props.ddh}
        loginID={props.loginID}
        returnID={props.returnID}
        friendlyID={props.friendlyID}
        showAddInterviewModal={props.interviewMode.showAddInterviewModal}
        loadSignaturePage={printFormName => props.formNav.navigateToSpecialForm(printFormName)}
        officeProfile={props.officeProfile}
        hasFLST={hasFLST}
        handleWizardStep={props?.handleStep}
        handleRequireFilingStatusDlg={handleRequireFilingStatusDlg}
        handleRemoteSignSelected={printFormName =>
          props.print.handleRemoteSignSelected(printFormName)
        }
        isSuperOrTechUserReadOnly={() => isSuperOrTechUserReadOnly(payload)}
        isLoading={props.isWizard ? null : props.spinner.isLoading}
        transmissionBlocked={get(props.officeProfile, 'transmission_blocked', false)}
        activeForm={props.formNav.activeForm}
        openTVAHelp={props.tva.openTVAHelp}
      />
    );
  }, [props, payload, calcAPI, interviewMode, hasFLST, handleRequireFilingStatusDlg]);

  let loadingContent;
  if (props.spinner.isLoading) {
    loadingContent = (
      <Spinner
        size={100}
        color="blue"
        loadingText={props.spinner.loadingText}
        textColor="white"
        bgColor="grey"
        lockActions={true} // do not allow user to click while loading
      />
    );
  } else {
    loadingContent = '';
  }

  const readyForReviewType = props.queueReturn.readyForReview ? 'Retract' : 'Submit';

  const { classes } = props;
  return (
    <MuiThemeProvider theme={appTheme}>
      {/* Prior Year Fee notice (CORP-1199). Rendered at the root so the
          acknowledgment sits above the whole return. In that mode the server has
          refused the lock, so the return underneath cannot be edited until the
          record is written. */}
      {priorYearNoticeForThisReturn && (
        <PriorYearNoticeModal
          notice={priorYearNoticeForThisReturn}
          onAcknowledge={handleAcknowledgePriorYearNotice}
          onDismiss={handleDismissPriorYearNotice}
        />
      )}
      <Prompt
        message={(location, action) => {
          handleBackButton(location, action);
          if (!props.isWizard) {
            return props.miscState.hasConfirmedClose;
          }
        }}
      />
      <div className={props.isWizard ? 'guided-estimator-mainContainer' : 'mainContainer'}>
        {/* This checks if it is a wizard return, other wise load header as usual */}
        {!props.isWizard && renderHeaderBar()}

        <AccountInfoBanner isInsideReturn={true} />

        <div className="rowC formviewer-container">
          {/* This checks if it is a regular return, other wise load return list as usual */}
          {formListReady() && !props.isWizard && (
            <div
              id="sideBarFormsContainer"
              style={props.formNav.assetMode ? { background: '#dddddd' } : { marginTop: '1em' }}
            >
              <SideBarForms
                assetMode={props.formNav.assetMode}
                openForm={props.formNav.getTaxForm}
                className="sidebarForms"
                formList={props.formList}
                attachedStatesCount={props.attachedStatesCount}
                onClickAddForm={(tab, index) => openAddFormList(tab, index)}
                handleSidebarFormClick={props.formNav.handleSidebarFormClick}
                activeForm={props.formNav.activeForm}
                handleDeleteForm={(form, desc) => props.depreciation.toggleDeleteDialog(form, desc)}
                handleDeletePkg={(pkg, desc) => props.depreciation.toggleDeletePkgDialog(pkg, desc)}
                returnProfile={props.returnProfile}
                detachDocument={props.documentAttach.onClickDetachDocument}
                getDocumentIDByVar={props.pdfAttach.getDocumentIDByVar}
                locked={props.isLockedFn()}
                showAddInterviewModal={props.interviewMode.showAddInterviewModal}
                addInterviewFlag={props.interviewMode.addInterviewFlag}
                tabListDefinitions={tabListDefinitions}
                isDemoAccount={props.usingDemoAccount}
                activeAccessLevels={props.activeAccessLevels}
                isBusiness={payload?.is_business}
              />
            </div>
          )}

          <div style={{ display: 'none' }}>
            {<canvas id="cnv" name="cnv" ref={cnvRef} width="480" height="100" />}
          </div>
          <div
            className="formContainer"
            onScroll={props.fieldEvents.scrollPositionInit}
            style={
              ['retn24', 'prin12', 'prin13', 'prin17', 'prin18', 'form12'].indexOf(
                props.formNav.activeForm,
              ) > -1
                ? styles.formStretch
                : styles.formAuto
            }
          >
            {!props.isWizard && props.isLockedFn() ? (
              <span className="row ovLabel" style={styles.lockedByMessage}>
                <img
                  src={lockIcon}
                  style={{
                    height: '26px',
                    marginTop: '0.2em',
                    padding: '0 1em',
                  }}
                />
                {getLockedMessage()}
              </span>
            ) : (
              <Fragment />
            )}

            {props.notificationsCount > 0 ? (
              <div id="NotificationContainer" style={{ marginBottom: '1em' }}>
                <Notifications
                  onFormViewer={true}
                  onClickViewReturn={props.switchReturn.onClickViewReturn}
                  removeMargins={true}
                />
              </div>
            ) : (
              <Fragment />
            )}

            <span className="centered-spinner">{loadingContent}</span>
            {loadSpecialForm()}
            <ButtonMenuElement
              buttonItems={props.fieldEvents.menuButtonsChoices}
              btnMenuOpen={props.buttonMenu.menuButtonOpen}
              openButtonMenu={props.buttonMenu.openButtonMenu}
              closeButtonMenu={props.buttonMenu.closeButtonMenu}
            />
          </div>
          {/* Check to see if current form is a specialFormLoad form, if so, we do not need fieldInfo button */}
          {![
            'retn18',
            'retn19',
            'retn24',
            'form12',
            'prin12',
            'prin13',
            'prin17',
            'prin18',
          ].includes(props.formNav.activeForm) && (
            <FieldInfo
              calcAPI={calcAPI}
              startSpin={() => props.spinner.startSpin(SPIN_LOAD_MSG)}
              currentView={props.currentView}
              currentEl={props.currentElRef?.current}
              activeForm={props.formNav.activeForm}
              restrictedFieldData={props.miscState.restrictedFieldData}
              fieldFormLinks={props.miscState.fieldFormLinks}
              getTaxForm={(form, currentEleID, wksType) =>
                props.formNav.getTaxForm(form, wksType, '', currentEleID)
              }
              isWizard={props.isWizard}
              taxYear={props.taxYear}
            />
          )}

          <Modal
            open={props.paymentCapture.returnPaymentOpen}
            onClose={() => props.paymentCapture.toggleReturnPayment(false)}
            disableBackdropClick={true}
          >
            <CapturePayment
              open={props.paymentCapture.returnPaymentOpen}
              onClose={() => props.paymentCapture.toggleReturnPayment(false)}
              returnPaymentInfo={props.paymentCapture.returnPaymentInfo}
              pollTerminalStatus={reqID => calcAPI.PayJunctionTerminalStatus(reqID)}
              terminalStatus={props.paymentCapture.paymentTerminalStatus}
              setTerminalStatus={props.paymentCapture.setPaymentTerminalStatus}
              processPaymentDetails={(a, b, c, d, e, f, g) => {
                calcAPI.ReturnProcessPaymentDetails(
                  a,
                  b,
                  c,
                  d,
                  e,
                  f,
                  props.formNav.activeForm,
                  g,
                  props.isReadOnly,
                );
              }}
              snackbarToggle={msg => props.snackbars.showPromptSnackbar(msg)}
              errorHandler={props.showErrorDialog}
              returnID={props.returnID}
              saveReturn={() => props.returnClose.saveReturn()}
              sendRemotePayment={props.wallet.SendRemotePayment}
              getReturnGUID={props.wallet.getReturnGUID}
              getWalletToken={props.wallet.getWalletToken}
              season={payload?.season}
            />
          </Modal>

          <Modal
            id="mdlAttachDocumentFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.tva.isVisionModalOpen}
            onClose={() => props.tva.setIsVisionModalOpen(false)}
            disableBackdropClick
          >
            <Paper elevation={5} classes={{ root: classes.addDocumentModal }}>
              <VisionAssistModal
                runTVASend={props.tva.runTVASend}
                addOrAttachDocument={props.pdfAttach.addOrAttachDocument}
                handleCloseVisionModal={() => props.tva.setIsVisionModalOpen(false)}
                buttonID={props.documentsToBeAttached.btnid}
                showSnackBarErr={msg => props.snackbars.showErrorSnackbar(msg)}
                TVAargType={props.tva.TVAargType}
                TVAprogress={props.tva.TVAprogress}
                clientFilingStatus={props.clientFilingStatus}
              />
            </Paper>
          </Modal>

          <Modal
            id="mdlAddFormModalFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.addFormModalOpen}
            onClose={() => props.toggleAddFormList()}
            disableBackdropClick={true}
          >
            <Paper
              elevation={5}
              style={isShortScreenRez ? styles.addFormModalShortScreen : styles.addFormModal}
            >
              <AddFormModal
                activeTab={props.addForm.activeAddFormTab}
                availableForms={props.addFormList}
                availableActivities={props.activityList}
                openAddFormList={(tab, index) => openAddFormList(tab, index)}
                updateAddFormList={pkg => props.addForm.updateAddFormList(pkg)}
                handleAddForm={form => handleAddForm(form)}
                handleAddFormIndex={form => handleAddFormIndex(form)}
                addAsset={asset => props.depreciation.handleAddAsset(asset)}
                handleCloseFormModal={() => props.toggleAddFormList()}
                formList={props.formList}
                tabListDefinitions={tabListDefinitions}
                isSuperOrTechUserReadOnly={() => isSuperOrTechUserReadOnly(payload)}
                isLocked={props.isLockedFn()}
                readyForReview={props.queueReturn.readyForReview}
                isFeederOffice={props.officeProfile.is_feeder_office}
                isBusiness={payload?.is_business}
                availablePackages={props.print.availablePackages}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlQueueReturnModalFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.queueReturn.queueReturnModalOpen}
            onClose={props.queueReturn.closeQueueReturnModal}
          >
            <Paper elevation={5} style={styles.queueReturnModal}>
              <QueueReturn
                transmitItemList={props.queueReturn.transmitItemList}
                feeItemList={props.queueReturn.feeItemList}
                feeTotal={props.queueReturn.feeTotal}
                walletBalance={props.queueReturn.walletBalance}
                wndwNote={props.queueReturn.wndwNote}
                updateTransmitList={props.queueReturn.updateTransmitList}
                closeQueueReturnModal={props.queueReturn.closeQueueReturnModal}
                closeQueueReturnModalDemo={props.queueReturn.closeQueueReturnModalDemo}
                closeReturn={
                  props.isLockedFn()
                    ? () => props.returnClose.closeLockedReturn()
                    : () => props.returnClose.closeAndSaveReturn()
                }
                submitReturnForReview={props.queueReturn.submitReturnReviewStatus}
                isFeeder={get(props.officeProfile, 'is_feeder_office', false)}
                transmissionBlocked={get(props.officeProfile, 'transmission_blocked', false)}
              />
            </Paper>
          </Modal>
          <SimpleDialog
            open={props.queueReturn.openDemoMessage}
            onConfirm={() => props.queueReturn.setOpenDemoMessage(false)}
            confirmText="OK"
            styled={true}
            dialogTitle="Demo Version"
            contentText="You are currently using a demo version of the program."
            // eslint-disable-next-line react/no-children-prop
            children="Please contact sales for more information:"
            extendedBody={
              <div>
                <div>
                  <b>Phone: </b>800.345.4337
                </div>
                <div>
                  <b>Fax: </b>209.835.2759
                </div>
                <div>
                  <b>Email: </b>sales@CrossLinkTax.com
                </div>
              </div>
            }
          />
          <SimpleDialog
            open={props.pdfModalSuccess}
            onConfirm={() => {
              props.setEncryptedPDFSuccessDialog(false);
            }}
            confirmText="OK"
            styled={true}
            dialogTitle={props.sentSuccessTitle}
            contentText={props.sentSuccessMsg}
          />
          <SimpleDialog
            open={props.print.noPrintDemoModal}
            onConfirm={() => {
              props.print.setNoPrintDemoModal(false);
            }}
            confirmText="OK"
            styled={true}
            dialogTitle="Demo Version"
            contentText="You are currently using a demo version of the program. Print functionality is not enabled. Please contact sales for more information:"
            extendedBody={
              <div>
                <div>
                  <b>Phone: </b>800.345.4337
                </div>
                <div>
                  <b>Fax: </b>209.835.2759
                </div>
                <div>
                  <b>Email: </b>sales@CrossLinkTax.com
                </div>
              </div>
            }
          />
          <Modal
            id="mdlAttachDocumentFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.documentAttach.addAttachmentModalOpen}
            onClose={props.documentAttach.closeAddAttachmentModal}
          >
            <Paper elevation={5} classes={{ root: classes.addDocumentModal }}>
              <AttachDocumentModal
                addOrAttachDocument={props.pdfAttach.addOrAttachDocument}
                handleCloseAttachDocumentModal={props.documentAttach.closeAddAttachmentModal}
                documentsToBeAttached={props.documentsToBeAttached.Attachments}
                buttonID={props.documentsToBeAttached.btnid}
                dataBin={props.documentsToBeAttached.data_bin}
                dataBinSig={props.documentsToBeAttached.data_bin_sig}
                attachments={props.attachments}
                showSnackBarErr={msg => props.snackbars.showErrorSnackbar(msg)}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlImportStockTransactions"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.importStock.showImportStock}
            onClose={props.importStock.closeImportStock}
            disableBackdropClick={true}
          >
            <Paper elevation={5} classes={{ root: classes.addDocumentModal }}>
              <ImportStockTransactions
                closeModal={props.importStock.closeImportStock}
                showImportMsg={msg => props.importStock.setImportStockMsg(msg)}
                uploadCSV={(file, spouse) => calcAPI.ReturnUploadCSV(file, spouse)}
                menuCommandResp={props.importStock.menuCommandResp}
                importStockMsg={props.importStock.importStockMsg}
                toggleBtnSpinner={toggle => props.importStock.setBtnClickSpinner(toggle)}
                btnClickSpinner={props.importStock.btnClickSpinner}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlChoiceListModalFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.choiceListModalOpen}
            onClose={() => props.toggleChoiceList(false)}
            disableBackdropClick={true}
          >
            <Paper elevation={5} style={styles.choiceListModal}>
              <ChoiceListModal
                choiceListColumns={props.choiceListColumns}
                choiceListData={props.choiceListData}
                choiceListTitle={props.choiceListTitle}
                handleChoiceListItem={(choiceItem, field) =>
                  props.choiceList.handleChoiceListItem(choiceItem, field)
                }
                handleCloseChoiceModal={toggle => props.toggleChoiceList(toggle)}
                choiceListIniField={props.choiceListIniField}
              />
            </Paper>
          </Modal>

          <Modal
            id="mdlremoteInvoiceModalFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.remoteInvoiceModalOpen}
            disableBackdropClick={true}
          >
            <Paper elevation={5} style={styles.remoteInvoiceModal}>
              <RemoteInvoiceModal
                sendRemotePayment={props.sendRemotePayment}
                onClose={() => store.dispatch(formViewerActions.toggleRemoteInvoiceModal(false))}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlAddReturnModalFormViewer"
            style={styles.addReturnModals}
            open={props.isTransferDataModalOpen}
            onClose={props.closeYTYDialog}
            disableBackdropClick={true}
          >
            <Paper elevation={5} style={styles.transferReturnModal}>
              <TransferData
                toggleYearToYearOptions={props.miscState.useYtyOptions}
                onYesTransferOption={props.transferData.onYesTransferDataOption}
                onCancelTransferData={props.transferData.onCancelTransferData}
                openCurrentYearModal={props.transferData.openCurrentYearModal}
                onYesTransfer={props.transferData.onYesTransferData}
                onNoTransfer={props.transferData.onNoTransferData}
                pyReturns={props.pyReturns}
                cyReturns={props.cyReturns}
                isBusiness={payload.is_business}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlErrorRejectModalFormViewer"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.errorRejectModalOpen}
            onClose={closeErrorRejectModal}
          >
            <Paper elevation={5} style={styles.errorRejectModal}>
              <ErrorRejectModal
                activeTab={props.verify.activeErrorRejectTab}
                verifyType={props.verify.verifyType}
                verifyList={props.verifyList}
                rejectList={props.rejectList}
                importErrorList={props.importErrorList}
                bankRejectList={props.bankRejectList}
                alertList={props.alertList}
                errors={props.errorNumber}
                warnings={props.warningNumber}
                handleVerify={(form, stmCode, file, field, desc, tab) =>
                  props.verify.handleVerify(form, stmCode, file, field, desc, tab)
                }
                handlePrint={(type, prefs, val, menuText) =>
                  props.print.handlePrint(type, prefs, val, menuText)
                }
                handleCloseFormModal={() => closeErrorRejectModal()}
                showVerifyModalSuccessDlg={props.showVerifyModalSuccessDlg}
                toggleVerifyModalDialog={props.toggleVerifyModalDialog}
                queueReturn={props.queueReturn.queueReturn}
                addFrontendNotification={props.addFrontendNotification}
                returnID={props.returnID}
                season={payload?.season}
                loadSignaturePage={printFormName => {
                  props.returnClose.saveReturn();
                  props.formNav.navigateToSpecialForm(printFormName);
                }}
                toggleRequestDocsModal={props.documentAttach.toggleRequestDocsModal}
                saveReturn={() => props.returnClose.saveReturn()}
                getTaxForm={props.formNav.getTaxForm}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlPrintComponentsModalFormViewer"
            aria-labelledby="print-components-title"
            aria-describedby="print-components-description"
            open={props.print.printComponentsModal}
            onClose={props.print.closePrintComponentsModal}
          >
            <Paper elevation={5} style={styles.printComponentsModal}>
              <PrintComponents
                isBusiness={payload?.is_business}
                prefs={props.print.print.prefs}
                handleClose={props.print.closePrintComponentsModal}
                handlePrint={(type, prefs, val, menuText) =>
                  props.print.handlePrint(type, prefs, val, menuText)
                }
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlPrintStateAcksModalFormViewer"
            aria-labelledby="print-state-acks-title"
            aria-describedby="print-state-acks-title"
            open={props.print.printStateAcksModal}
            onClose={props.print.closePrintStateAcksModal}
          >
            <Paper elevation={5} style={styles.printStateAcksModal}>
              <PrintStateAcks
                stateAcks={props.stateAcks}
                stateAcksPType={props.print.stateAcksPType}
                handleClose={props.print.closePrintStateAcksModal}
                handlePrint={(type, prefs, val, menuText) =>
                  props.print.handlePrint(type, prefs, val, menuText)
                }
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlPrintStateClientLettersFormViewer"
            aria-labelledby="print-state-client-letters-title"
            aria-describedby="print-state-client-letters-title"
            open={props.print.printStateClientLettersModal}
            onClose={props.print.closePrintStateClientLetters}
          >
            <Paper elevation={5} style={styles.printStateAcksModal}>
              <PrintStateClientLetters
                states={props.attachedStates}
                handleClose={props.print.closePrintStateClientLetters}
                handlePrint={(type, prefs, val, menuText) =>
                  props.print.handlePrint(type, prefs, val, menuText)
                }
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlEventLogFormViewer"
            aria-labelledby="event-log-title"
            open={props.eventLogModal}
            onClose={() => props.toggleEventLogModal(false)}
          >
            <Paper elevation={5} style={styles.eventLogModal}>
              <EventLog
                returnID={props.returnID}
                pssn={props.primarySSNEIN}
                printMeta={printMeta}
                toggleModal={toggle => props.toggleEventLogModal(toggle)}
                locked={payload.is_tech_support_and_impersonating}
                readyForReview={props.queueReturn.readyForReview}
                isFeederOffice={props.officeProfile.is_feeder_office}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlK1ManagerModalFormViewer"
            aria-labelledby="k1Manager-modal-title"
            open={props.isK1ManagerModalOpen}
            onClose={() => props.toggleK1ManagerModal(false)}
            disableBackdropClick={true}
          >
            <Paper elevation={5} style={styles.k1ManagerModal}>
              <K1ManagerModal
                open={props.isK1ManagerModalOpen}
                toggleModal={toggle => props.k1Manager.handleToggleK1ManagerModal(toggle)}
                k1List={props.k1Manager.k1List}
                k1pkgList={props.k1Manager.k1pkgList}
                handleListK1s={pkg => {
                  props.k1Manager.setK1List([]);
                  calcAPI.ReturnListK1s(pkg);
                }}
                handleAddK1={() => calcAPI.ReturnAddK1()}
                handleChangeHideK1s={flag => props.k1Manager.handleChangeHideK1s(flag)}
                handleDeleteK1={ocr => calcAPI.ReturnDeleteK1(ocr)}
                handleEmailK1={ocr => calcAPI.ReturnEmailK1(ocr)}
                navToK1={ocr => calcAPI.ReturnFormLoad(ocr, '', '', '')}
                hideK1Flag={props.k1Manager.hideK1Flag}
                printMeta={printMeta}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlAddAsset"
            open={props.addAssetModal}
            onClose={() => props.toggleAddAssetModal(false)}
          >
            <Paper elevation={5} style={styles.addAssetModal}>
              <AddAsset
                toggleModal={toggle => props.toggleAddAssetModal(toggle)}
                assetDetails={props.depreciation.selectedAsset}
                addNewAsset={(id, type, desc, date, assetCode) =>
                  props.depreciation.addNewAsset(id, type, desc, date, assetCode)
                }
              />
            </Paper>
          </Modal>
          <Dialog
            id="mdlPrintDialog"
            aria-labelledby="print-dialog-modal-title"
            aria-describedby="print-dialog-modal-description"
            open={!!props.printDialog}
            onClose={() => props.togglePrintDialog(false)}
            PaperProps={{ tabIndex: -1 }}
            disableAutoFocus={true}
            disableBackdropClick={true}
          >
            <PrintDialog
              style={styles.printDialog}
              pType={props.print.print.pType}
              formOccur={props.print.print.formOccur}
              prefs={props.print.print.prefs}
              reqSigs={props.print.print.reqSigs}
              pActionEligible={props.print.print.pActionEligible}
              menuText={props.print.print.menuText}
              handleClose={() => props.togglePrintDialog(false)}
              handlePrintSelected={() => props.print.handlePrintActionsPrintSelected()}
              handleSigningInOffice={() => props.print.handleSigningInOffice()}
              handleRemoteSignSelected={() => props.print.handleRemoteSignSelected()}
              handleEncryptedPDFSelected={() => props.encryptedPDF.handleEncryptedPDFSelected()}
              printPreferences={props.print.print.prefs}
              onScreen={props.onScreen}
              setPreferredSignaturePad={signaturePadType =>
                props.setPreferredSignaturePad(signaturePadType)
              }
              handlePrintPDFWarningMessage={handlePrintPDFWarningMessage}
              printPDFWarningMessage={props.printPDFWarningMessage}
              loginID={props.loginID}
              returnStatus={props.returnStatus}
            />
          </Dialog>
          <RemoteSignatureModal
            handleSigningRemotely={docType => props.print.handleSigningRemotely(docType)}
            handleSigningInOffice={(sigDocID, sigs, docType) =>
              props.print.handleSigningInOffice(sigDocID, sigs, docType)
            }
            preparerInfo={payload?.preparer_info}
            drilldownHistory={props.ddh}
            signatureRequestDataRef={signatureRequestDataRef}
            returnPrintQueryLoading={props.print.returnPrintQueryLoading}
          />
          <Modal
            id="mdlSignatureCapture"
            aria-labelledby="signature-capture-modal-title"
            aria-describedby="signature-capture-modal-description"
            open={props.signatureCapture}
            onClose={() => {
              props.toggleSignatureCaptureModal(false);
              store.dispatch(formViewerActions.setRMSflag(false));
              props.signatureRequestActiveSet(false);
              props.print.setPendingSignatureModal(false);
            }}
          >
            <DialogContent>
              <SignatureCapture
                currentSig={props.print.print.currentSig}
                consent={props.print.print.consent}
                handleSignatureCapture={(sigType, sigValue, padType) =>
                  props.print.handleSignatureCapture(sigType, sigValue, padType)
                }
                handleClose={() => {
                  props.toggleSignatureCaptureModal(false);
                  store.dispatch(formViewerActions.setRMSflag(false));
                  props.signatureRequestActiveSet(false);
                  props.print.setPendingSignatureModal(false);
                }}
              />
            </DialogContent>
          </Modal>
          <Modal
            id="mdlSendTextLinkMessage"
            open={!!props.textLinkModalOpen}
            onClose={() => props.toggleTextLinkSendModal(false, '')}
            style={{ top: '20%' }}
          >
            <Paper
              elevation={5}
              style={{
                width: '40vw',
                padding: '0',
                position: 'relative',
                margin: 'auto',
                overflow: 'auto',
              }}
            >
              <TextLinkSendModal
                toggleTextLinkSendModal={() => props.toggleTextLinkSendModal(false, '')}
                textLinkModalContent={
                  props.textLinkModalContentOriginal || props.textLinkModalContent
                }
                handleSendTextLinkMessage={(name, number, carrier, message, carrierDomain) =>
                  props.textLink.handleSendTextLinkMessage(
                    name,
                    number,
                    carrier,
                    message,
                    carrierDomain,
                  )
                }
                returnID={props.returnID}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlReceivedTextLinkMessages"
            open={!!props.textLinkReceivedModalOpen}
            onClose={() => props.toggleTextLinkReceivedModal(false)}
            style={{ top: '20%' }}
          >
            <Paper
              elevation={5}
              style={{
                width: '60vw',
                padding: '0',
                position: 'relative',
                margin: 'auto',
                overflow: 'auto',
              }}
            >
              <TextLinkReceivedModal
                toggleTextLinkSendModal={props.toggleTextLinkSendModal}
                handleSendTextLinkMessage={(name, number, carrier, message, carrierDomain) =>
                  props.textLink.handleSendTextLinkMessage(
                    name,
                    number,
                    carrier,
                    message,
                    carrierDomain,
                  )
                }
                setUnreadTextMessageCount={setUnreadTextMessageCount}
              />
            </Paper>
          </Modal>
          <Modal
            id="mdlAddArchiveDocumentModalDocArchive"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.documentAttach.isPreviewModalOpen}
            onClose={props.documentAttach.closePreviewModal}
            className={classes.modalSpacing}
          >
            <Paper className={classes.modal}>
              <Typography variant="body1" className={classes.modalTitle}>
                Final Tax Return
              </Typography>
              <div className="preview-doc-archive-modal-container">
                <table>
                  <tbody>
                    <tr>
                      <td className="preview-doc-archive-modal-container-table-td">
                        <a
                          className="preview-doc-archive-modal-container-table-td-a"
                          onClick={props.documentAttach.openRequestPDFModal}
                        >
                          <img
                            className="preview-doc-archive-modal-container-table-td-a-img"
                            src={downloadIcon}
                          />
                          <button
                            id="mdlDocumentArchiveDownloadDocBtn"
                            className="preview-doc-archive-modal-container-table-td-a-btn"
                          >
                            Download Document
                          </button>
                        </a>
                      </td>
                      <td>
                        <Button
                          id="mdlDocumentArchiveDownloadDocCloseBtn"
                          variant="contained"
                          color="primary"
                          className={classes.alertModalButton}
                          onClick={props.documentAttach.closePreviewModal}
                        >
                          Close
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <object
                            width="950"
                            height="650"
                            id="downloadArchiveDoc"
                            data={props.documentAttach.currentOpenedAttachment}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Paper>
          </Modal>
          <Modal
            id="mdlAddArchiveDocumentModalDocArchive"
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={props.documentAttach.isPDFAlertModalOpen}
          >
            <Paper className={classes.modal}>
              <Typography className={classes.modalTitle} color="inherit" variant="body1">
                Print PDF
              </Typography>
              <div className="preview-doc-archive-modal-container">
                <Typography variant="body1" className={classes.alertModalBody}>
                  {REQUEST_PDF_MSG}
                </Typography>
                <div className={classes.alertModalButtons}>
                  <Button
                    id="mdlDocumentArchiveDownloadDocCloseBtn"
                    variant="contained"
                    color="primary"
                    className={classes.alertModalButton}
                    onClick={props.documentAttach.closeRequestPDFModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="mdlDocumentArchiveDownloadDocCloseBtn"
                    variant="contained"
                    color="primary"
                    className={classes.alertModalButton}
                    href={props.documentAttach.currentOpenedAttachment}
                    onClick={props.documentAttach.closeRequestPDFModal}
                    download="document"
                  >
                    Ok
                  </Button>
                </div>
              </div>
            </Paper>
          </Modal>
          <Dialog
            open={!!props.textLinkNotActivatedDlg}
            onClose={() => props.toggleTextLinkNotActivatedDialog(false)}
            disableBackdropClick={true}
          >
            <DialogTitle>TextLink Not Configured</DialogTitle>
            <DialogContent>
              <p>
                TextLink has not been configured for this office. Use the office setup pages to
                configure this feature.
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                id="btnTextLinkNotActivated"
                onClick={() => props.toggleTextLinkNotActivatedDialog(false)}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={!!props.textLinkNoClients}
            onClose={() => props.toggleShowNoTextLinkClientsDlg(false)}
            disableBackdropClick={true}
          >
            <DialogTitle>Missing Required Data</DialogTitle>
            <DialogContent>
              <p>
                TextLink cannot be used until taxpayer and/or spouse cell phone information has been
                provided on the Client Data Screen.
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                id="btnTextLinkNotNoClientsOK"
                onClick={() => props.toggleShowNoTextLinkClientsDlg(false)}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={props.switchReturn.showSwitchReturnDlg}
            onClose={() => props.switchReturn.setShowSwitchReturnDlg(false)}
            disableBackdropClick={true}
          >
            <DialogTitle>Switch Returns</DialogTitle>
            <DialogContent>
              <p>
                You are attempting to view another return. Do you want to save the current return
                before you continue to the return?
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                id="btnFormViewerSwitchReturnsSave"
                onClick={() => props.switchReturn.switchReturn(true)}
              >
                Save and Continue
              </Button>
              <Button
                autoFocus
                id="btnFormViewerSwitchReturnsNoSave"
                onClick={() => props.switchReturn.switchReturn(false)}
              >
                Continue without Saving
              </Button>
              <Button
                autoFocus
                id="btnFormViewerSwitchReturnsCancel"
                onClick={() => props.switchReturn.setShowSwitchReturnDlg(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
          <BookmarkField
            bookmarks={props.bookmarks.bookmarks}
            desc={props.bookmarks.bookmarks.currBookmarkDesc}
            timestamp={props.bookmarks.bookmarks.currBookmarkTimestamp}
            fieldID={props.bookmarks.bookmarks.bookmarkFldID}
            setBookmarkFldEditor={props.bookmarks.setBookmarkFldEditor}
            setBookmark={props.bookmarks.setBookmarkByFldID}
            snackbarToggle={msg => props.snackbars.showPromptSnackbar(msg)}
            isEditable={props.formNav.activeForm !== 'retn18'}
          />
          <Dialog
            open={props.miscState.requireFilingStatusDlg}
            onClose={() => props.miscState.closeRequireFilingStatusDlg()}
            disableBackdropClick={true}
          >
            <DialogTitle>Filing Status Required</DialogTitle>
            <DialogContent>Please provide a filing status to continue.</DialogContent>
            <DialogActions>
              <Button
                id="btnRequireFLST"
                onClick={() => props.miscState.closeRequireFilingStatusDlg()}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={props.depreciation.confirmDeleteDialogOpen}
            onClose={() => props.depreciation.toggleDeleteDialog(null, null)}
            disableBackdropClick={true}
            aria-labelledby="confirm-delete-title"
            aria-describedby="confirm-delete-desc"
          >
            <DialogTitle id="confirm-delete-title">
              Remove {props.depreciation.removeFormDesc}
            </DialogTitle>
            <DialogContent id="confirm-delete-desc">
              {props.depreciation.removeFormDlg}
            </DialogContent>
            <DialogActions>
              {props.depreciation.removeFormIrremovable ? (
                <>
                  <Button
                    id="btnCancelDeleteFormViewer"
                    onClick={() => props.depreciation.setConfirmDeleteDialogOpen(false)}
                  >
                    OK
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    id="btnCancelDeleteFormViewer"
                    onClick={() => props.depreciation.setConfirmDeleteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="btnDoDeleteFormViewer"
                    onClick={() =>
                      props.depreciation.handleDeleteForm(props.depreciation.removeForm)
                    }
                  >
                    Delete
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>
          <Dialog
            open={props.depreciation.confirmAssetDelete}
            onClose={() => props.depreciation.toggleAssetDeleteDialog()}
            disableBackdropClick={true}
            aria-labelledby="confirm-asset-delete-title"
            aria-describedby="confirm-asset-delete-desc"
          >
            <DialogTitle id="confirm-asset-delete-title">
              Delete Activity Item: {props.depreciation.removeFormDesc}
            </DialogTitle>
            <DialogContent id="confirm-asset-delete-desc">
              Preparing to delete ALL assets and related forms associated with this activity. This
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button
                id="btnCancelDeleteAssetFormViewer"
                onClick={() => props.depreciation.toggleAssetDeleteDialog()}
              >
                Cancel
              </Button>
              <Button
                id="btnDoDeleteAssetFormViewer"
                onClick={() =>
                  props.depreciation.handleDeleteActivityForm(props.depreciation.removeForm)
                }
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          <SimpleDialog
            open={props.depreciation.confirmPkgDelete}
            disableBackdropClick={true}
            onClose={() => props.depreciation.toggleDeletePkgDialog(null, null)}
            onConfirm={() => props.depreciation.handleDeletePkg(props.depreciation.removePkgId)}
            confirmText="Delete"
            styled={true}
            dialogTitle="Remove State"
            extendedBody={
              <div>
                {props.depreciation.removePkgDesc} has been selected for removal. This cannot be
                undone.
              </div>
            }
          />
          <SimpleDialog
            open={props.missingInfoModalOpen}
            onConfirm={() => {
              props.toggleMissingInfoModal(false);
            }}
            confirmText="OK"
            styled={true}
            dialogTitle="Information Missing"
            extendedBody={
              <div>
                <div style={{ marginTop: '0.5vh' }}>
                  <div>
                    <b>Taxpayer Information Missing</b>
                  </div>
                  <div>{ENCRYPTED_PDF_ERROR_MESSAGES.MISSING_TAXPAYER_INFO_EMAIL}</div>
                </div>
                {props.spouseInfoAvailable && (
                  <div style={{ marginTop: '0.5vh' }}>
                    <div>
                      <b>Spouse Information Missing</b>
                    </div>
                    <div>{ENCRYPTED_PDF_ERROR_MESSAGES.MISSING_SPOUSE_INFO_EMAIL}</div>
                  </div>
                )}
              </div>
            }
          />
        </div>
        <Dialog
          open={props.messageDialogOpen}
          disableBackdropClick={true}
          onClose={props.closeMsgDialog}
        >
          <MessageDialog
            messageDialogInfo={props.messageDialogInfo}
            handleCloseDialog={() => props.closeMsgDialog()}
            handleResponseFromDialog={(e, a, b) => handleResponseFromDialog(e, a, b)}
          />
        </Dialog>
        <Dialog
          open={!!props.errorDialog}
          disableBackdropClick={true}
          onClose={props.hideErrorDialog}
        >
          <DialogTitle id="error-dialog-title">
            {props.errorDialogMessage === 'Form Imported or Not Supported'
              ? ''
              : props.errorDialogTitle}
          </DialogTitle>
          <DialogContent id="error-dialog-desc">{props.errorDialogMessage}</DialogContent>
          <DialogActions>
            <Button id="btnErrorDialogFormViewer" onClick={props.hideErrorDialog}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={props.fieldEvents.showDblBlind}
          aria-labelledby="dblind-dialog-title"
          aria-describedby="dblind-dialog-description"
        >
          <DialogTitle id="dblindTitle">Double entry validation</DialogTitle>
          <DialogContent>
            <DialogContentText>Please reenter data</DialogContentText>
            <TextField
              autoFocus={true}
              margin="dense"
              id="dblindTextField"
              variant="filled"
              fullWidth
              onKeyPress={e => {
                if (e.key === 'Enter') props.fieldEvents.handleDblindOk();
              }}
              onBlur={e => props.fieldEvents.handleDblindOk(e)}
            />
          </DialogContent>
          <DialogActions>
            <div style={styles.buttonGroup}>
              <Button
                id="dblindOkButton"
                color="primary"
                style={styles.leftButton}
                onClick={props.fieldEvents.handleDblindOk}
                onKeyPress={e => {
                  if (e.key === 'Enter') props.fieldEvents.handleDblindOk();
                }}
              >
                Ok
              </Button>
              <Button
                id="dblindCancelButton"
                color="primary"
                style={styles.button}
                onClick={props.fieldEvents.handleDblindCancel}
                onKeyPress={e => {
                  if (e.key === 'Enter') props.fieldEvents.handleDblindCancel();
                }}
              >
                Cancel
              </Button>
            </div>
          </DialogActions>
        </Dialog>
        <Dialog open={props.fieldEvents.showConsentForm} disableBackdropClick={true}>
          <DialogTitle id="consent-dialog-title">
            {'Consent to receive tax office alerts via text messages?'}
          </DialogTitle>
          <DialogContent id="consent-dialog-desc">
            Taxpayer must be informed of the following:
            <br />
            • Message frequency varies per tax office.
            <br />
            • Message and data rates may apply.
            <br />
            • Text STOP to opt out or HELP for help.
            <br />
            <div
              className="terms-and-conditions-link"
              onClick={() => {
                props.textLink.setIsTextlinkPDFOpen(true);
                props.textLink.setTextlinkPDF('textTermsAndConditionsPDF');
              }}
            >
              Click for Terms and Conditions
            </div>
            <div
              className="terms-and-conditions-link"
              onClick={() => {
                props.textLink.setIsTextlinkPDFOpen(true);
                props.textLink.setTextlinkPDF('textingPrivacyPolicyPDF');
              }}
            >
              Click for Privacy Policy
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              id="agreeConsentBtn"
              color="primary"
              onClick={() => {
                props.consentModal.hideConsentModal();
              }}
            >
              Agree
            </Button>
            <Button
              id="disagreeConsentBtn"
              color="primary"
              onClick={() => {
                props.consentModal.hideConsentModal(true);
              }}
            >
              Disagree
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={props.returnClose.confirmReturnClose}
          disableBackdropClick={true}
          onClose={() => props.returnClose.hideConfirmClose()}
        >
          <DialogTitle id="confirm-return-close">Close Return</DialogTitle>
          <DialogContent id="confirm-return-close-desc">
            {'Do you want to save changes to the Tax Return?'}
          </DialogContent>
          <DialogActions>
            <Button
              id="btnConfirmReturnClose"
              onClick={() => {
                props.returnClose.setConfirmReturnClose(false);
                props.returnClose.closeAndSaveReturn();
              }}
            >
              Save
            </Button>
            <Button
              id="btnConfirmReturnClose"
              onClick={() => {
                props.returnClose.setConfirmReturnClose(false);
                props.returnClose.closeReturn();
              }}
            >
              Don&apos;t Save
            </Button>
            <Button
              id="btnConfirmReturnClose"
              onClick={() => props.returnClose.setConfirmReturnClose(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={props.returnClose.confirmDeleteReturn}
          disableBackdropClick={true}
          onClose={() => props.returnClose.hideConfirmDeleteReturn()}
        >
          <DialogTitle id="confirm-delete-return">Delete Return</DialogTitle>
          <DialogContent id="confirm-delete-return-desc">
            {'Are you sure you want to delete this return? This cannot be undone.'}
          </DialogContent>
          <DialogActions>
            <Button
              id="btnYesConfirmDeleteReturn"
              onClick={() => {
                props.returnClose.setConfirmDeleteReturn(false);
                props.returnClose.deleteReturn();
              }}
            >
              Yes
            </Button>
            <Button
              id="btnNoConfirmDeleteReturn"
              onClick={() => {
                props.returnClose.setConfirmDeleteReturn(false);
              }}
            >
              No
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={props.remoteSignSuccessful}
          onEntered={() => props.spinner.stopSpin()}
          disableBackdropClick={true}
          onClose={() => props.returnClose.hideConfirmClose()}
        >
          <DialogTitle id="confirm-return-close">Remote Signature Request</DialogTitle>
          <DialogContent id="confirm-return-close-desc">
            {'The remote signature request has been sent.'}
          </DialogContent>
          <DialogActions>
            <Button
              id="btnCompletedRemoteSignClose"
              onClick={() => {
                props.remoteSignSuccessfulSet(false);
                props.signatureRequestActiveSet(false);
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={props.textLink.showTextLinkSendErrorDialog}
          onClose={() => props.textLink.setShowTextLinkSendErrorDialog(false)}
          disableBackdropClick={true}
        >
          <DialogTitle>Message Error</DialogTitle>
          <DialogContent>
            {props.textLink.textlinkErrorMessage && (
              <>
                <div>{props.textLink.textlinkErrorMessage}</div>
                <br></br>
              </>
            )}
            <div>{TEXTLINK_ERROR_MESSAGE}</div>
          </DialogContent>
          <DialogActions>
            <Button
              id="btnTextMessageFail"
              onClick={() => props.textLink.setShowTextLinkSendErrorDialog(false)}
            >
              Yes
            </Button>
            <Button
              id="btnNoConfirmDeleteReturn"
              onClick={() => {
                props.returnClose.setConfirmDeleteReturn(false);
              }}
            >
              No
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!props.textlinkSendSuccess}
          onClose={() => props.toggleTextlinkConfirmSentDlg(false)}
          disableBackdropClick={true}
        >
          <DialogTitle>Message Sent</DialogTitle>
          <DialogContent>The message was sent successfully.</DialogContent>
          <DialogActions>
            <Button
              id="btnTextMessageSucceed"
              onClick={() => {
                props.toggleTextlinkConfirmSentDlg(false);
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <SimpleDialog
          open={props.copyReturn.dupeSSNCopyRtnOpen}
          onClose={() => props.copyReturn.setDupeSSNCopyRtnOpen(false)}
          onConfirm={() => {
            props.copyReturn.copyToTraining();
            props.copyReturn.setDupeSSNCopyRtnOpen(false);
          }}
          dialogTitle="Copy To Training"
          contentText="The tax return already exists in the training database. Do you want to overwrite?"
        />
        <Dialog
          open={!!props.showVerifySuccessDlg}
          disableBackdropClick={true}
          onClose={() => {
            props.toggleVerifyDialog(false);
            props.closeErrorRejectModal();
          }}
        >
          <DialogTitle>Verify Success</DialogTitle>
          <DialogContent>{'The return has been verified successfully.'}</DialogContent>
          <DialogActions>
            <Button
              id="btnFormViewerVerifyOK"
              onClick={() => {
                props.toggleVerifyDialog(false);
                props.closeErrorRejectModal();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <SimpleDialog
          open={props.copyReturn.dupeSSNCopyLiveRtnOpen}
          onClose={() => props.copyReturn.setDupeSSNCopyLiveRtnOpen(false)}
          onConfirm={() => {
            props.copyReturn.copyToLive();
            props.copyReturn.setDupeSSNCopyLiveRtnOpen(false);
          }}
          dialogTitle="Copy To Live"
          contentText="The tax return already exists in the live database. Do you want to overwrite?"
        />
        <SimpleDialog
          open={props.calcError.calcErrorPopup}
          onConfirm={props.calcError.hideCalcError}
          confirmText="OK"
          dialogTitle={props.calcError.calcErrorTitle}
          contentText={props.calcError.calcErrorMsg}
        />
        <SimpleDialog
          open={!!props.submitForReviewDlg}
          onClose={() => props.toggleSubmitForReviewDlg(false)}
          onConfirm={props.submitForReview.handleConfirmSubmitForReview}
          confirmText="OK"
          dialogTitle={`${readyForReviewType} Return From Review`}
          contentText={`Are you sure you want to ${readyForReviewType} this return from review?`}
        />
        <SimpleDialog
          open={props.print.openPrintDemoMessage}
          onConfirm={() => {
            props.print.setOpenPrintDemoMessage(false);
          }}
          confirmText="OK"
          styled={true}
          dialogTitle="Demo Version"
          contentText="You are currently using a demo version of the program. The print options are disabled."
          // eslint-disable-next-line react/no-children-prop
          children="Please contact sales for more information:"
          extendedBody={
            <div>
              <div>
                <b>Phone: </b>800.345.4337
              </div>
              <div>
                <b>Fax: </b>209.835.2759
              </div>
              <div>
                <b>Email: </b>sales@CrossLinkTax.com
              </div>
            </div>
          }
        />
        <SimpleDialog
          open={props.tva.openTVADisallow}
          onConfirm={() => props.tva.setOpenTVADisallow(false)}
          confirmText="OK"
          dialogTitle="Agent Assist"
          contentText="The Agent Assist service is not available for your account."
        />
        <Dialog
          id="mdlEmailEncryptedPdfSaveDialog"
          aria-labelledby="encrypted-pdf-save-modal-title"
          aria-describedby="encrypted-pdf-save-modal-description"
          disableBackdropClick={true}
          open={props.suggestedPswSaveDialog}
          onClose={() => props.toggleSaveSuggestedPassword(false)}
          PaperProps={{ tabIndex: -1 }}
          disableAutoFocus={true}
        >
          <Paper elevation={5}>
            <SuggestedPasswordSaveDialog
              handleClose={() => props.toggleSaveSuggestedPassword(false)}
              handleSaveSuggestedPassword={() => props.encryptedPDF.handleSaveSuggestedPassword()}
            />
          </Paper>
        </Dialog>
        <Dialog
          id="mdlEmailEncryptedPdfDialog"
          aria-labelledby="encrypted-pdf-modal-title"
          aria-describedby="encrypted-pdf-modal-description"
          disableBackdropClick={true}
          open={props.suggestedPswDialog}
          onClose={() => {
            props.toggleSuggestedPassword(false);
            props.setArchiveDocId('');
          }}
          PaperProps={{ tabIndex: -1 }}
          disableAutoFocus={true}
        >
          <Paper elevation={5}>
            <SuggestedPasswordDialog
              isOpen={props.suggestedPswDialog}
              encryptedPwd={props.encryptedPassword !== undefined ? props.encryptedPassword : ''}
              encryptedTitle={props.encryptedPdfTitle !== undefined ? props.encryptedPdfTitle : ''}
              taxpayerEmail={
                props.taxpayerEmailForPdf !== undefined ? props.taxpayerEmailForPdf : ''
              }
              tpCellPhoneNumber={
                props.taxpayerCellPhoneNumber !== undefined ? props.taxpayerCellPhoneNumber : ''
              }
              tpCellPhoneDomain={
                props.taxpayerCellPhoneDomain !== undefined ? props.taxpayerCellPhoneDomain : ''
              }
              tpCellPhoneCarrier={
                props.taxpayerCellPhoneCarrier !== undefined ? props.taxpayerCellPhoneCarrier : ''
              }
              tpAllowText={props.taxpayerAllowText !== undefined ? props.taxpayerAllowText : ''}
              spAllowText={props.spouseAllowText !== undefined ? props.spouseAllowText : ''}
              spouseEmail={props.spouseEmailForPdf !== undefined ? props.spouseEmailForPdf : ''}
              spCellPhoneNumber={
                props.spouseCellPhoneNumber !== undefined ? props.spouseCellPhoneNumber : ''
              }
              spCellPhoneCarrier={
                props.spouseCellPhoneCarrier !== undefined ? props.spouseCellPhoneCarrier : ''
              }
              spCellPhoneDomain={
                props.spouseCellPhoneDomain !== undefined ? props.spouseCellPhoneDomain : ''
              }
              showSpouseOption={
                props.spouseInfoAvailable !== undefined ? props.spouseInfoAvailable : false
              }
              preparerEmail={
                props.preparerEmailForPdf !== undefined ? props.preparerEmailForPdf : ''
              }
              isTextLinkOn={
                props.isTextLinkActivated !== undefined ? props.isTextLinkActivated : false
              }
              handleClose={() => {
                props.toggleSuggestedPassword(false);
                props.setArchiveDocId('');
              }}
              handleOptionNotAvailable={() => {
                props.toggleSuggestedPassword(false);
                props.setArchiveDocId('');
                props.toggleMissingInfoModal(true);
              }}
              setEncryptedPassword={(
                newEncryptedPassword,
                encryptedPdfTitle,
                taxpayerSelected,
                spouseSelected,
                representativeSelected,
                taxpayerSelectedEPass,
                spouseSelectedEPass,
                representativeSelectedEPass,
                taxpayerSelectedText,
                spouseSelectedText,
                representativeSelectedText,
              ) =>
                props.encryptedPDF.handleNewEncryptedPassword(
                  newEncryptedPassword,
                  encryptedPdfTitle,
                  taxpayerSelected,
                  spouseSelected,
                  representativeSelected,
                  taxpayerSelectedEPass,
                  spouseSelectedEPass,
                  representativeSelectedEPass,
                  taxpayerSelectedText,
                  spouseSelectedText,
                  representativeSelectedText,
                )
              }
            />
          </Paper>
        </Dialog>
        <Dialog
          id="mdlEmailEncryptedPdfSaveDialog"
          aria-labelledby="encrypted-pdf-send-modal-title"
          aria-describedby="encrypted-pdf-send-modal-description"
          disableBackdropClick={true}
          open={props.encryptedPdfDialogModalOpen}
          onClose={() => props.toggleEncryptedPdf(false)}
          PaperProps={{ tabIndex: -1 }}
          disableAutoFocus={true}
        >
          <Paper elevation={5}>
            <SuggestedPasswordDoc
              handleClose={() => {
                props.toggleEncryptedPdf(false);
                props.print.setShowFinalPDF(true);
              }}
              handleSaveSuggestedPasswordDoc={() =>
                props.encryptedPDF.handleSaveSuggestedPasswordDoc()
              }
              htmlPreview={props.emailBlob}
              isOpen={props.encryptedPdfDialogModalOpen}
            />
          </Paper>
        </Dialog>
        <PreviewTermsAndConditions
          isPDFOpen={!!props.textLink.isTextlinkPDFOpen}
          toggleModal={() => {
            props.textLink.setIsTextlinkPDFOpen(false);
            props.textLink.setTextlinkPDF('');
          }}
          textlinkPDF={props.textLink.textlinkPDF}
        />
        <Snackbar
          open={props.snackbars.promptSnackbar}
          onClose={() => props.snackbars.hidePromptSnackbar()}
          ContentProps={{
            'aria-describedby': 'prompt-msg',
            className: 'snackbar-prompt',
          }}
          message={<div id="prompt-msg">{props.snackbars.promptSnackbarMessage}</div>}
        />
        <Snackbar
          open={props.bookmarks.isNotesAlertOpen}
          onClose={() => props.bookmarks.setIsNotesAlertOpen(false, { notesFlag: false })}
          ContentProps={{
            'aria-describedby': 'prompt-msg',
            className: 'snackbar-info',
          }}
          message={<div id="prompt-msg">{NOTES_REMINDER}</div>}
        />
        <SimpleDialog
          open={props.interviewMode.addInterviewModalFlag}
          onConfirm={() => props.interviewMode.addInterviewConfirm()}
          confirmText="Confirm"
          onClose={() => props.interviewMode.addInterviewCancel()}
          styled={true}
          dialogTitle="Confirm Inteview Forms"
          contentText="Please confirm you want to add interview forms to the return. Otherwise, click cancel."
        />
      </div>

      <RefreshToken socketWorker={props.socketWorkerRef?.current} />
    </MuiThemeProvider>
  );
};

// Named exports consumed by FormViewerWrapper
export { mapStateToProps, mapDispatchToProps };

// Default export: function component wrapped only in withStyles so it
// receives `props.classes`. Connect + withRouter live in FormViewerWrapper.
export default withStyles(styles)(FormViewer);
